import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import MultiSelectFilter, {
  matchesSelection,
} from "./MultiSelectFilter";
import { gapTargetFor, rowUnder } from "./dragTarget";
import { gestureFrom } from "./panGesture";
import { useNavigate, useParams } from "react-router-dom";
import styles from "./styles/AssetManagement.module.css";
import { UserData } from "../../../Context/DataContext";
import { LanguageContext } from "../../../Context/changeLanguageProvider";
import { getAssetTypeTranslated } from "../../../Utils/translationUtils";
import { formatStoredDate } from "../../../Utils/formatStoredDate";
import {
  readTableView,
  writeTableView,
} from "../../../Utils/partnerTableView";
import { selectLogo } from "../Assets/selectLogo";
import downArrow from "../down.svg";
import upArrow from "../up.svg";
import upDownArrow from "../updownarrow.svg";
import AddAsset from "../Add Asset/AddAsset";
import OwnershipModal from "./OwnershipModal";
import DebtModal from "./DebtModal";
import PeopleModal from "./PeopleModal";
import {
  FINANCIAL_EXPOSURE_OPTIONS,
  FINANCIAL_EXPOSURE_CUSTOM,
  DEBT_TYPE_GROUPS,
} from "../Asset Evaluation/AssetEvaluation";
import { Beneficiarytrustee } from "../Beneficiarytrustee/Beneficiarytrustee";
import {
  latestValueOf,
  latestDebtOf,
  latestCurrencyOf,
  latestDebtCurrencyOf,
  applyInlineValueEdit,
  resolveExposureSelection,
  canonicalAssetType,
  resolveAssetTypeSelection,
  ASSET_TYPE_CUSTOM,
  mergeEvaluationIntoAsset,
  evaluationFromAsset,
  displayAmount,
  formatCompactMoney,
  CURRENCY_SYMBOLS,
  toInputDate,
  toStoredDate,
  ownerOptionsFrom,
  resolveOwnersSelection,
  resolveOwnerName,
  ORDER_FIELDS,
  sortByStoredOrder,
  reorderAssets,
  moveAssetBy,
} from "../../../Utils/assetEvaluationUtils";
import { defaultCurrencyFor } from "../Asset Evaluation/AssetEvaluation";
import Dropdown from "../Asset Evaluation/Dropdown";
import { RandomId } from "../../../Utils/RandomId";
import editIcon from "../Inheritance Tree/styles/img/edit_icon.svg";
import useWindowDimensions from "../../Footer/footer";
import EditAsset from "../Edit Asset/EditAsset";
import ApprovalModal from "../Partner Approval/ApprovalModal";
import { needsReview } from "../../../Utils/partnerApproval";
import { getPartner } from "../Partner Sharing/PartnerSharing";
import { PageTabs } from "../PartnerNav/PageTabs";
import {
  useInCustomerProfile,
  usePartnerView,
} from "../PartnerNav/partnerNav";
import partnerNavStyles from "../PartnerNav/styles/PartnerNav.module.css";
import { customerPath } from "../PartnerNav/partnerNav";

/*
  Asset Management — a full-width table of every asset with its evaluation
  fields. Reached from the "Assets Management" button on the Assets page and
  from the top navigation.

  Everything here filters and sorts on the client: the whole asset list is
  already in context, and this page deliberately has no pagination.
*/

const EMPTY = "-";

/*
  Asset types available in the filter, mirroring Assets/TableFilter.jsx. These
  are the stored `asset_type` strings — the label the user sees comes from
  `getAssetTypeTranslated`, so renaming a type never touches this list.
*/
const ASSET_TYPES = [
  "Bank Account",
  "Crypto Wallet",
  "Life Insurance",
  "Investment Account",
  "Company Shares",
  "NFT",
  "Password Manager",
  "Password",
  "Online storage",
  "Real Estate",
  "Retirement Plan",
  "Physical Vault",
  "Your Will",
  "Company",
  "Vehicle",
  "Precious Objects",
  "Business Ownership",
  "Loans Receivable",
  "Cash",
  "Intellectual Property",
  "Employee Benefits",
  "Other",
];

/*
  Fixed columns; custom-field columns are appended per the user's own data.

  The order here is the order of the table, and the six in the first group are
  the ones a new user starts with — see DEFAULT_PRESENTED_FIELDS. Liquid no
  longer starts on, but it stays up here with the headline figures rather than
  down among the debt fields: it describes the asset, not its debt.
*/
const BASE_COLUMNS = [
  /*
    Permanent: a row with no name is not identifiable. Every other column
    describes the asset; this one IS the asset, so it is not offered in
    "Presented fields" and cannot be switched off.
  */
  {
    key: "asset_name",
    labelKey: "assetManagementText4",
    sortable: true,
    permanent: true,
  },
  { key: "asset_type", labelKey: "assetManagementText5", sortable: true },
  { key: "financial_exposure", labelKey: "assetManagementText6" },
  { key: "asset_value", labelKey: "assetManagementText7", sortable: true },
  { key: "debt_value", labelKey: "assetManagementText13", sortable: true },
  { key: "is_liquid", labelKey: "assetManagementText26", sortable: true },
  /*
    Off until asked for. Ownership is one block read two ways, so the two halves
    stay adjacent. Then the debt detail, edited in place exactly like Asset
    Name; then when it last changed; then the two that open a picker, the way
    Ownership and Debt already do on this table.
  */
  { key: "my_ownership", labelKey: "assetManagementText8" },
  { key: "other_owners", labelKey: "assetManagementText33" },
  { key: "debt_type", labelKey: "assetManagementText27", sortable: true },
  { key: "debt_annual_interest", labelKey: "assetManagementText28", sortable: true },
  { key: "debt_payoff_date", labelKey: "assetManagementText29", sortable: true },
  /*
    Read-only, and the only column here nobody types into: it is written by
    the save effect in DataContext whenever anything about the asset changes
    -- a rename, a new valuation, a beneficiary added or removed. See
    Utils/assetLastUpdated.js.
  */
  { key: "last_updated", labelKey: "assetManagementText42", sortable: true },
  { key: "beneficiaries", labelKey: "assetManagementText34" },
  { key: "trustees", labelKey: "assetManagementText35" },
];

/*
  What a user who has never opened "Presented fields" sees: what an asset is,
  what it is worth, what is owed against it, and who it concerns. Everything
  else — the ownership split, the debt detail, the last update, whether it can be
  turned into cash — is there to be switched on, not to be switched off.

  Order is not taken from this list: the table draws the columns in
  BASE_COLUMNS order and this only says which of them start on. The two people
  columns are last there, so they land after the money, ahead of the edit
  column, which is permanent and cannot be switched off at all.

  Custom-field columns are not in here either, so they start off as well.
*/
const DEFAULT_PRESENTED_FIELDS = [
  "asset_name",
  "asset_type",
  "financial_exposure",
  "asset_value",
  "debt_value",
  "beneficiaries",
  "trustees",
];

/*
  Sorted as numbers rather than text, or "10" would land before "3". The two
  money columns are handled separately: they sort on the underlying record, not
  on the compacted string the cell shows.
*/
const NUMERIC_COLUMNS = new Set(["debt_annual_interest"]);

/*
  Sorted on the stored value rather than the one in the cell.

  These columns show a date in the reader's format (MM/DD/YYYY in English,
  DD/MM/YYYY in French). Compared as text that orders by whichever part comes
  first, so December 2024 would sit above January 2026. The stored YYYY/MM/DD
  sorts correctly as plain text, which is why it is what gets compared.
*/
const RAW_SORT_COLUMNS = new Set(["last_updated"]);

/*
  The row action. Always last, always present: it is excluded from "Presented
  fields" and cannot be hidden, because hiding it would leave no way to open an
  asset from this page.
*/
const ACTIONS_COLUMN = { key: "actions", labelKey: null, permanent: true };

/*
  Reordering on touch.

  The rows are reordered by HTML5 drag-and-drop, which touch browsers do not
  implement — there is no `dragstart` from a finger, so the gesture goes to the
  scroller and the row never moves. Below the breakpoint each row gets a grip in
  its own column, driven by pointer events instead.

  The grip is the only thing on the page with `touch-action: none`, so it is the
  only thing that takes a drag rather than a scroll. Press anywhere else and the
  table still scrolls exactly as before.

  First, not last: the table scrolls horizontally on a phone, and the actions
  column sits off the right edge until you scroll to it. Also permanent, so it
  cannot be switched off in "Presented fields" and leave no way to reorder.
*/
const REORDER_COLUMN = { key: "reorder", labelKey: null, permanent: true };

/*
  The row's rounded start.

  A 20px sliver, frozen to the card's left edge, holding nothing. It is the
  mirror of the pinned edit cell at the other end, and it exists for the same
  reason: a rounded end has to be PAINTED by something that stays at the edge
  of the card. The edit cell is frozen, so it travels the row and keeps its
  corner wherever the edge is; the row's real start had nothing holding it, so
  it scrolled away and the clip cut every row square the moment the table
  moved sideways.

  20px because that is the corner radius -- any narrower and the two curves
  would not fit inside it.

  First, ahead of the touch grip, because it is the row's edge. Permanent, so
  it cannot be switched off in "Presented fields" and leave the rows cut
  square again.
*/
const EDGE_COLUMN = { key: "edge", labelKey: null, permanent: true };

/*
  Every column that cannot be switched off, in one place — the two icon columns
  and the asset's own name. Consulted by both isVisible and toggleColumn so the
  two cannot disagree about what is hideable.
*/
/*
  The true rendered width of a heading, not an estimate.

  The column sizing elsewhere multiplies a character count by an average
  advance width, which is fine when the result only has to be roughly right.
  It is not fine for the two avatar columns: their headings decide the width
  outright, and the estimate overshoots by 15-29px — enough to leave the
  avatars sitting in visibly empty space.

  Canvas measures the string in the face that will actually draw it. Cached
  because the answer only changes when the label or the font does, and this is
  called from a memo that re-runs whenever the rows do.
*/
const headingWidthCache = new Map();
const HEADING_FONT = '600 16px "SF Pro Display", -apple-system, system-ui, sans-serif';

const measureHeading = (label) => {
  const text = String(label || "");
  if (!text) return 0;
  const cached = headingWidthCache.get(text);
  if (cached !== undefined) return cached;
  let width = text.length * 8.1; // the old estimate, if canvas is unavailable
  try {
    const canvas =
      measureHeading.canvas ||
      (measureHeading.canvas = document.createElement("canvas"));
    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.font = HEADING_FONT;
      width = ctx.measureText(text).width;
    }
  } catch (error) {
    /* keep the estimate */
  }
  headingWidthCache.set(text, width);
  return width;
};

const PERMANENT_KEYS = new Set(
  [...BASE_COLUMNS, ACTIONS_COLUMN, REORDER_COLUMN, EDGE_COLUMN]
    .filter((column) => column.permanent)
    .map((column) => column.key)
);

/*
  One row's worth of vertical space: the 48px cell plus the 2px of
  `border-spacing` between cards. What a row shifts by when it moves aside for
  one being dragged past it on a phone.
*/
const ROW_SLOT = 66;

/*
  The shortest the table is allowed to look, counted in those same slots.

  Ten, because the page below it should not come up to meet the reader when a
  filter leaves two rows or a search leaves none. The table is the page; a
  footer arriving halfway up the screen reads as the page having broken
  rather than as the list being short.
*/
const FLOOR_ROWS = 10;

/*
  How tall a dropdown opened from inside a row may get before it scrolls. The
  type list is long — every built-in plus this user's own — and left to its own
  height it covered the rows the edit is being made against.
*/
const CELL_MENU_HEIGHT = 250;

/*
  The gap sits ABOVE a row, named by that row's id -- so the one place a gap
  cannot be named that way is after the last row, and this stands for it.

  It doubles as the drop target: reorderAssets looks the target up among the
  rows and appends when it finds none, so handing it a value no row can have
  puts the dragged row at the end. That is the only way to reach the last
  position, since every other drop inserts BEFORE its target.
*/
const DROP_AT_END = "__drop_at_end__";

// Where the step buttons replace dragging.
const COMPACT_WIDTH = 860;

const FALLBACK = {
  assetManagementText1: "Asset Management",
  assetManagementText2: "Download Financial Report",
  assetManagementText3: "Asset Management Page",
  assetManagementText4: "Asset Name",
  assetManagementText5: "Asset Type",
  assetManagementText6: "Financial exposure",
  assetManagementText7: "Asset Value",
  assetManagementText8: "My Ownership",
  assetManagementText9: "Search",
  assetManagementText10: "Presented fields",
  assetManagementText11: "No assets match these filters",
  assetManagementText12: "Wealth Report",
  assetManagementText13: "Debt Value",
  assetManagementText14: "Edit ownership",
  assetManagementText21: "Drag to reorder",
  assetManagementText22: "Back to the list",
  assetManagementText23:
    "Choose a financial exposure before recording a value",
  assetManagementText24: "Add debt",
  assetManagementText26: "Liquid",
  assetManagementText27: "Type of Debt",
  assetManagementText28: "Annual Interest",
  assetManagementText29: "Payoff Date",
  assetManagementText42: "Last update",
  assetManagementText33: "Other Owners",
  assetManagementText34: "Beneficiaries",
  assetManagementText35: "Trusted Advisors",
  assetManagementText36: "Who this asset goes to",
  assetManagementText38: "You already have an asset with this name",
  assetManagementText39: "Edit asset",
  assetManagementText40: "Clear filter",
};

/*
  Compact table value. Symbol placement is currency-dependent — see
  formatCompactMoney in Utils/assetEvaluationUtils.js, shared with the Assets
  table so the two cannot drift.
*/
const formatValue = (value, currencyCode) =>
  formatCompactMoney(value, currencyCode);

/*
  Whether a cell actually holds a figure. The debt cell pins a leading "-" as
  soon as it is focused, so a bare sign is not an amount and must not count as
  one — otherwise merely clicking into Debt Value would demand an exposure.
*/
const hasAmount = (raw) => String(raw ?? "").replace(/-/g, "").trim() !== "";

/*
  Whether the row editor currently holds a usable exposure. "Custom" without a
  name is not one: it is dropped on save (see commitEdit), so treating it as
  set would let a figure through against an exposure that never gets stored.
*/
const exposureSetOn = (draft) => {
  const value = String(draft.financial_exposure || "").trim();
  if (value === "") return false;
  if (value !== FINANCIAL_EXPOSURE_CUSTOM) return true;
  return String(draft.financial_exposure_custom_label || "").trim() !== "";
};

/*
  The debt shown in the column is the LAST ADDED debt record, not the
  asset-level scalar — the same rule the Asset Value column follows. Delete
  every debt record and the column empties.
*/
function debtAmountOf(asset) {
  const latest = latestDebtOf(asset);
  if (latest === "" || latest === null || latest === undefined) return null;
  const amount = Number(latest);
  if (Number.isNaN(amount)) return null;
  return -Math.abs(amount);
}

/*
  Writes a set of ownership percentages into an asset's "other owners" data
  entry, creating the entry when the asset never had one.

  Shared by the local-override merge and by the commit, so what the cell shows
  while the row is open and what is finally stored cannot disagree.
*/
const mergeOwnership = (asset, patch, fallbackId) => {
  const entries = asset.data_entries || [];
  const at = entries.findIndex((e) => e && e.type === "other_owners");
  /*
    Clearing drops the whole block rather than blanking its fields, which is
    what removing the "Other owners of this asset" field does in the asset
    modal — an entry with no owners and no percentage is not the same thing as
    no entry, and only the second reads as "I own all of this".
  */
  if (patch.cleared) {
    if (at < 0) return asset;
    return { ...asset, data_entries: entries.filter((_, i) => i !== at) };
  }
  const entry = {
    ...(at >= 0 ? entries[at] : { id: fallbackId }),
    type: "other_owners",
    my_ownership:
      patch.my_ownership !== undefined
        ? patch.my_ownership
        : (at >= 0 && entries[at].my_ownership) || "",
    owners:
      patch.owners !== undefined ? patch.owners : (at >= 0 && entries[at].owners) || [],
  };
  return {
    ...asset,
    data_entries:
      at >= 0 ? entries.map((e, i) => (i === at ? entry : e)) : [...entries, entry],
  };
};

// The "other owners" block, if the asset has one.
function ownersEntry(asset) {
  return (asset.data_entries || []).find((e) => e.type === "other_owners");
}

// Every custom field on an asset, as { title, value }.
function customFieldsOf(asset) {
  return (asset.data_entries || [])
    .filter((e) => e.type === "custom")
    .flatMap((e) => e.custom_fields || [])
    .filter((f) => f && String(f.title || "").trim() !== "");
}

function AssetManagement() {
  // Only a partner sees the wider title spacing; see PartnerNav.
  const partnerView = usePartnerView();
  const inCustomerProfile = useInCustomerProfile();
  const navigate = useNavigate();
  const userData = UserData();
  const websiteCurrentLang = useContext(LanguageContext).language;

  const [content, setContent] = useState(
    websiteCurrentLang?.content?.User?.AssetManagement?.AssetManagement ||
      FALLBACK
  );
  const [filterContent, setFilterContent] = useState(
    websiteCurrentLang?.content?.User?.Assets?.TableFilter
  );
  const [dublicatedcontent, setDublicatedContent] = useState(
    websiteCurrentLang?.content?.DublicatedText
  );
  useEffect(() => {
    setContent(
      websiteCurrentLang?.content?.User?.AssetManagement?.AssetManagement ||
        FALLBACK
    );
    setFilterContent(websiteCurrentLang?.content?.User?.Assets?.TableFilter);
    setDublicatedContent(websiteCurrentLang?.content?.DublicatedText);
  }, [websiteCurrentLang]);

  const t = (key) => (content && content[key]) || FALLBACK[key];

  const assets = useMemo(
    () => (userData && userData.UserData && userData.UserData.assets) || [],
    [userData]
  );

  /*
    Set only when this page is rendered inside a customer's profile, from the
    /user/customers/:id/:tab route. Empty on the partner's own Asset
    Management.
  */
  const { id: customerId } = useParams();
  /*
    ---- a partner's own view of this table ----

    A partner opening a customer's profile gets the default set of columns --
    the customer's own choice is not among the things shared with a partner,
    see My Customers/sharedCustomer.js -- and could not change it: "Presented
    fields" saves through `userData.setUserData` and `userData.Update`, and on
    this side both are noops (CustomerDetail builds that object), so a tick
    simply did nothing.

    A partner's view is their own, not an edit to the customer's account, so
    it is held in this component and in sessionStorage under the customer's id
    -- see Utils/partnerTableView. It survives a refresh and ends with the
    tab, the customer's saved columns are never written, and nothing about it
    is sent anywhere.

    Only for a partner. On the customer's own page `viewKey` is null, every
    filter starts empty exactly as before, and their columns keep being saved
    to their account.
  */
  const partnerReadOnly = Boolean(userData && userData.partnerReadOnly);
  const viewKey = partnerReadOnly ? customerId || null : null;
  const restored = useMemo(() => readTableView(viewKey), [viewKey]);

  const [search, setSearch] = useState(() => restored?.search || "");
  /*
    Arrays, not single values: these filters take several choices at once. An
    EMPTY array means "no filter" -- see matchesSelection, where that contract
    lives, because reading it as "match nothing" empties the table.
  */
  const [assetTypes, setAssetTypes] = useState(() => restored?.assetTypes || []);
  const [exposures, setExposures] = useState(() => restored?.exposures || []);
  const [sort, setSort] = useState(
    () => restored?.sort || { key: null, desc: false }
  );
  /*
    The partner's own presented-fields list, or null for "I have not chosen" --
    which is what falls back to the customer's. An empty array is a choice (a
    partner who switched everything off) and is kept as one.
  */
  const [partnerFields, setPartnerFields] = useState(
    () => restored?.fields ?? null
  );

  /*
    Which customer the five pieces of state above currently describe. Set by
    the reset below, read by the save, so that the moment the route changes
    neither of them acts on the other customer's view.
  */
  const viewKeyRef = useRef(viewKey);

  /*
    Kept for the session on every change, rather than on the way out: a
    refresh and a closed tab never run an unmount, and a refresh is the case
    this exists for.
  */
  useEffect(() => {
    if (!viewKey || viewKeyRef.current !== viewKey) return;
    writeTableView(viewKey, {
      fields: partnerFields,
      search,
      assetTypes,
      exposures,
      sort,
    });
  }, [viewKey, partnerFields, search, assetTypes, exposures, sort]);

  /*
    Moving from one customer to another in the same session. The route param
    changes under a component that stays mounted, so without this the second
    customer is shown the first one's view -- and the save above would then
    write it under the second one's name.
  */
  useEffect(() => {
    if (viewKeyRef.current === viewKey) return;
    viewKeyRef.current = viewKey;
    const next = readTableView(viewKey);
    setPartnerFields(next?.fields ?? null);
    setSearch(next?.search || "");
    setAssetTypes(next?.assetTypes || []);
    setExposures(next?.exposures || []);
    setSort(next?.sort || { key: null, desc: false });
  }, [viewKey]);
  /*
    ---- Manual row order ----

    Each asset carries its own position in `asset_management_order`, saved with
    the rest of the asset. Assets that predate the field fall back to their
    array position, so an account that has never dragged anything looks exactly
    as it did — see sortByStoredOrder in Utils/assetEvaluationUtils.js.

    Column sorting and manual order stay mutually exclusive: dragging clears any
    active sort, and sorting ignores the manual order while it is on.
  */
  const [dragId, setDragId] = useState(null);
  const [dragOverId, setDragOverId] = useState(null);
  const [fieldsOpen, setFieldsOpen] = useState(false);
  /*
    ---- Presented fields ----

    Saved per user in `asset_management_presented_fields` as the list of columns
    to SHOW, and encrypted with the rest of the user's data — one user's choice
    is never visible to another.

    Stored as "show these" rather than "hide these" deliberately: absent or null
    means the starting set in DEFAULT_PRESENTED_FIELDS, which is what a user who
    has never opened the menu has. A column added to the table later is off for
    them until they ask for it, and an old saved list keeps meaning exactly the
    columns it names.
  */
  const customerFields =
    (userData && userData.UserData &&
      userData.UserData.asset_management_presented_fields) || null;
  /*
    A partner sees their own choice once they have made one. Until then this
    falls through to whatever the shared payload carried, which today is
    nothing at all -- so the default set, as before.

    `??` and not `||`: an empty list is a choice, not an absence.
  */
  const presentedFields = partnerReadOnly
    ? partnerFields ?? customerFields
    : customerFields;
  const [openAdd, setOpenAdd] = useState(false);
  /*
    The asset whose approval interstitial is open. Not the same thing as
    assetToEdit: the customer sees the explanation first, and only then the
    ordinary edit modal.
  */
  const [reviewingAsset, setReviewingAsset] = useState(null);
  const approvalContent =
    websiteCurrentLang?.content?.User?.PartnerApproval?.PartnerApproval;
  // The asset whose Edit Asset modal is open, from the row action column.
  const [assetToEdit, setAssetToEdit] = useState(null);
  /*
    Asset names must be unique, the same rule the Add / Edit Asset modals
    enforce (see EditAsset.jsx). Inline renaming bypassed it entirely, so two
    rows could be given the same name. Holds the row id that failed, or null.
  */
  const [nameError, setNameError] = useState(null);
  /*
    A figure recorded against an asset — a valuation or a debt — is a figure OF
    something, so neither can be entered on an asset with no financial
    exposure.

    Holds `{ id, field }`: which row tried, and which of the two cells it was,
    so the message can be shown under the cell the user actually typed in. That
    matters because the Financial exposure column can be hidden from "Presented
    fields", and an error rendered only there would be invisible.
  */
  const [exposureRequired, setExposureRequired] = useState(null);
  /*
    "Custom" is a prompt for a name, not a type: unnamed it resolves to no type
    at all, and the commit then quietly kept whatever type the row already had.
    Holds the row id that tried it, or null — the row stays locked until the
    name is filled in, the same way a clashing name locks it.
  */
  const [typeNameRequired, setTypeNameRequired] = useState(null);
  /*
    A debt entered through the debt modal, held here rather than saved.

    The modal used to write straight to the server, which meant a debt survived
    a refresh even though the row edit that produced it was never finished — and
    even though ending that edit could still be refused for want of an exposure.
    Nothing about a row is stored until the edit ends, so this waits with the
    rest of it: `{ id, asset }`, the row and its asset with the debt merged in.

    Discarded by Escape, exactly like the inline cell edits beside it.
  */
  const [pendingDebt, setPendingDebt] = useState(null);


  /*
    ---- Row edit lock ----

    At most one row is editable at a time. Clicking a cell puts that whole row
    into edit mode; clicking anywhere else commits it and releases the lock, so
    the table never fills up with loose inputs. While a row is locked the rest
    of the table dims, which is the visual cue for "you are editing this one".

    Escape cancels, Enter commits. Ownership is excluded — it opens a modal
    (see OwnershipModal) because its percentages need validating together.
  */
  /*
    Who this asset can name as an owner — built by the same helper the Add /
    Edit Asset panel uses, so the two lists cannot diverge.

    Three things this fixes at once. Owners are stored as plain NAMES, not
    `beneficiary:<id>` / `asset:<id>`: that is what the panel has always stored,
    and rows written from this table were the odd ones out. All three
    company-shaped types qualify, not just "Company Shares". And the asset being
    edited is excluded — a company cannot be one of its own other owners.
  */
  const ownerOptionsFor = (asset) =>
    ownerOptionsFrom(
      (userData && userData.UserData) || {},
      asset && (asset.id != null ? asset.id : asset.asset_id)
    );

  /*
    A frozen account cannot edit, and neither can a partner looking at somebody
    else's assets -- reading what a customer shared is not permission to change
    it. One flag, so every gate already keyed on this covers both.
  */
  const disabledEditing =
    Boolean(userData && userData.isFreezed) || partnerReadOnly;
  /*
    Narrow enough that reordering has to be tapped rather than dragged. Measured
    rather than done in CSS: the column has to exist in the colgroup and the
    width calculation too, not just be hidden.
  */
  const { width: viewportWidth } = useWindowDimensions();
  const compact = viewportWidth <= COMPACT_WIDTH;
  const [editingId, setEditingId] = useState(null);
  const [draft, setDraft] = useState(null);
  // Edits are held here until there is an endpoint to save them to.
  const [overrides, setOverrides] = useState({});
  const [ownershipFor, setOwnershipFor] = useState(null);
  // The asset whose debt modal is open, for a Debt Value cell with no debt yet.
  const [debtFor, setDebtFor] = useState(null);
  // The row whose beneficiaries / trusted advisors are being picked.
  const [peopleFor, setPeopleFor] = useState(null);
  /*
    Ownership entered in the modal, held rather than saved.

    The modal opens from inside the row edit now, so its result has to wait with
    everything else typed into that row: saving on the spot would mean Escape
    discarded every other cell but kept this one.
  */
  const [pendingOwnership, setPendingOwnership] = useState(null);
  const editRowRef = useRef(null);
  /*
    Raised the moment "Custom" is picked, so the name input that replaces the
    dropdown takes focus. A plain autoFocus would misfire: on an asset that is
    already custom the input mounts together with the rest of the row and would
    steal focus from the asset name.
  */
  const exposureFocusRef = useRef(false);
  // Same, for the asset-type cell's free-text field.
  const assetTypeFocusRef = useRef(false);
  /*
    The column whose editor should take focus when a row opens: the one that was
    clicked. Cleared as soon as it is used, so a re-render for any other reason
    does not steal focus back.
  */
  const focusColumnRef = useRef(null);
  /*
    Raised when a click away from the open row closed it, and consumed by the
    beginEdit that same click triggers.

    Clicking another row while one is being edited should only close the one
    being edited — the second click is what opens the new one. The state cannot
    be read for this: the mousedown commits and clears `editingId`, React
    re-renders before the click event, and by the time the cell's handler runs
    it looks like no row was open at all.
  */
  const closedByClickAwayRef = useRef(false);
  const focusOn = (key) => (el) => {
    if (!el || focusColumnRef.current !== key) return;
    focusColumnRef.current = null;
    // Focusing normally scrolls the field into view; this is what must not.
    el.focus({ preventScroll: true });
  };

  const rowId = (asset, index) =>
    asset.id || asset.asset_id || `row_${index}`;

  /*
    Writes one row's inline edits into the real asset and saves.

    Editing Asset Value records a NEW valuation dated today rather than
    overwriting the asset's figure, because the column shows the latest record
    and the chart needs a point. Editing it twice in one day replaces that day's
    record instead of stacking two — see applyInlineValueEdit.

    Returns false when the row cannot be matched to an asset, so the caller can
    keep the edit on screen rather than discarding it.
  */
  const persistRowEdit = (rowKey, patch) => {
    const root = (userData && userData.UserData) || {};
    const assets = root.assets || [];
    const index = assets.findIndex(
      (asset, i) => rowId(asset, i) === rowKey
    );
    if (index < 0) return false;

    /*
      A debt entered in the modal is merged into the asset but not saved, so it
      is part of what this commit is writing — not something already stored. It
      also has to be the baseline the value comparisons below run against, or
      the debt it already recorded would be recorded a second time.
    */
    const current =
      pendingDebt && pendingDebt.id === rowKey ? pendingDebt.asset : assets[index];
    let next = { ...current };

    /*
      Ownership entered in the modal during this edit, saved with the row.

      A row left on "Custom" carries the typed name in `owner_custom_label`;
      resolving here turns it into the row's owner, adds it to this user's own
      owner list and drops the transient field, so the asset stores a name and
      the next asset offers it in the dropdown.
    */
    let ownerOptionsNext = root.asset_owner_custom_options || [];
    if (pendingOwnership && pendingOwnership.id === rowKey) {
      let staged = pendingOwnership;
      if (!staged.cleared && Array.isArray(staged.owners)) {
        const resolved = resolveOwnersSelection(
          staged.owners,
          ownerOptionsNext,
          ownerOptionsFor(current)
        );
        staged = { ...staged, owners: resolved.owners };
        ownerOptionsNext = resolved.options;
      }
      next = mergeOwnership(next, staged, RandomId(10));
    }

    if (patch.asset_name !== undefined) next.asset_name = patch.asset_name;

    /*
      Asset-level fields with nothing to resolve and no history to write: the
      cell holds the value, so the value is the edit. `is_liquid` is a real
      boolean here — encryptAsset is what turns it into a string.
    */
    for (const field of [
      "debt_type",
      "debt_annual_interest",
      "debt_payoff_date",
    ]) {
      if (patch[field] !== undefined) next[field] = patch[field];
    }
    if (patch.is_liquid !== undefined) next.is_liquid = patch.is_liquid === true;
    /*
      Lists of user ids, exactly as the Add / Edit Asset modal stores them —
      encryptAsset already handles both fields, so nothing new is written here.
    */
    for (const field of ["beneficiaries", "trustees"]) {
      if (Array.isArray(patch[field])) next[field] = patch[field];
    }

    /*
      A typed custom type becomes one of this user's own, or selects an existing
      one rather than duplicating it — the same resolver the Add / Edit Asset
      modals use, so the two cannot disagree.
    */
    let assetTypeOptionsNext =
      ((userData && userData.UserData) || {}).asset_type_custom_options || [];
    if (patch.asset_type !== undefined) {
      const resolved = resolveAssetTypeSelection(
        patch.asset_type,
        patch.asset_type_custom_label,
        assetTypeOptionsNext
      );
      // Custom picked without a name resolves to "": leave the type as it was.
      if (resolved.asset_type) next.asset_type = resolved.asset_type;
      assetTypeOptionsNext = resolved.options;
    }

    /*
      Exposure is sent as a pair. Picking Custom and typing a name creates or
      reuses one of this user's own exposure types, exactly as the modal does.
    */
    let exposureOptions = root.financial_exposure_custom_options || [];
    if (patch.financial_exposure !== undefined) {
      const resolved = resolveExposureSelection(
        {
          financial_exposure: patch.financial_exposure,
          financial_exposure_custom_label: patch.financial_exposure_custom_label,
        },
        exposureOptions
      );
      /*
        Written even when blank: commitEdit above has already refused a clear on
        an asset that has readings, so a blank arriving here is a deliberate one
        on an asset that has none.
      */
      next.financial_exposure = resolved.financial_exposure;
      exposureOptions = resolved.options;
    }

    // Value edits become records, not scalar overwrites.
    /*
      A record is only written when the value actually changed. The draft is
      seeded from the current figures, so passing them through unconditionally
      would stamp a fresh record dated today every time the row was committed —
      including when the user only edited the name or the exposure.

      Exposure is deliberately not part of this: it describes the asset, so
      changing it updates the field and adds nothing to the history or the chart.
    */
    const seededValue =
      latestValueOf(current) === "" ? "" : String(latestValueOf(current));
    const seededDebt =
      debtAmountOf(current) == null ? "" : String(debtAmountOf(current));
    next = applyInlineValueEdit(
      next,
      {
        asset_value:
          String(patch.asset_value ?? "") === seededValue
            ? ""
            : patch.asset_value,
        debt_amount:
          String(patch.debt_amount ?? "") === seededDebt
            ? ""
            : patch.debt_amount,
      },
      defaultCurrencyFor(websiteCurrentLang?.label)
    );

    // Custom-field cells are edited in place inside their data entry.
    if (patch.custom) {
      next.data_entries = (next.data_entries || []).map((entry) =>
        entry.type === "custom"
          ? {
              ...entry,
              custom_fields: (entry.custom_fields || []).map((field) => {
                const title = String(field.title).trim();
                return Object.prototype.hasOwnProperty.call(patch.custom, title)
                  ? { ...field, value: patch.custom[title] }
                  : field;
              }),
            }
          : entry
      );
    }

    const nextAssets = assets.map((asset, i) => (i === index ? next : asset));
    userData.setUserData({
      ...root,
      assets: nextAssets,
      financial_exposure_custom_options: exposureOptions,
      asset_type_custom_options: assetTypeOptionsNext,
      asset_owner_custom_options: ownerOptionsNext,
    });
    userData.Update(RandomId(10));
    return true;
  };

  /*
    Case-insensitive, ignoring the asset being renamed — identical to the check
    in EditAsset.jsx so the two paths cannot disagree.
  */
  const nameClashes = (rowKey, name) => {
    const trimmed = String(name || "").trim();
    if (!trimmed) return false;
    const assets = ((userData && userData.UserData) || {}).assets || [];
    return assets.some(
      (asset, i) =>
        rowId(asset, i) !== rowKey &&
        String(asset.asset_name || "").toLowerCase() === trimmed.toLowerCase()
    );
  };

  /*
    Returns whether the row was released. It refuses to close on a clashing name
    or a missing exposure, and callers need to tell that apart from a clean
    close — see the click-away handler.
  */
  const commitEdit = () => {
    if (editingId && draft) {
      /*
        A clashing name keeps the row in edit mode rather than being discarded
        or saved — the same outcome as the modal, where Save is refused until it
        is resolved.
      */
      if (
        draft.asset_name !== undefined &&
        nameClashes(editingId, draft.asset_name)
      ) {
        setNameError(editingId);
        return false;
      }
      setNameError(null);

      /*
        Custom asset type with no name typed: refused for the same reason the
        Add / Edit Asset modals refuse it. resolveAssetTypeSelection returns no
        type for an unnamed Custom, and the write below falls back to the type
        the row already had — so the row would close having silently kept a type
        the user had just replaced.
      */
      if (
        draft.asset_type === ASSET_TYPE_CUSTOM &&
        String(draft.asset_type_custom_label || "").trim() === ""
      ) {
        setTypeNameRequired(editingId);
        return false;
      }
      setTypeNameRequired(null);

      /*
        Entering a value or a debt with no exposure is refused: the record about
        to be written would be a valuation of nothing, and a debt is owed
        against an asset whose exposure says what that asset is. Checked against
        the figures the editor was seeded with, so only an actual change trips
        it — the same rule the Add / Edit Asset modal applies.
      */
      const rowAsset = ((userData && userData.UserData) || {}).assets?.find(
        (a, i) => rowId(a, i) === editingId
      );
      if (rowAsset) {
        const seededValue =
          latestValueOf(rowAsset) === "" ? "" : String(latestValueOf(rowAsset));
        const seededDebt =
          debtAmountOf(rowAsset) == null ? "" : String(debtAmountOf(rowAsset));
        const valueChanged =
          String(draft.asset_value ?? "") !== seededValue &&
          hasAmount(draft.asset_value);
        // A lone "-" is the sign the debt cell pins, not an amount.
        const debtChanged =
          String(draft.debt_amount ?? "") !== seededDebt &&
          hasAmount(draft.debt_amount);
        if (!exposureSetOn(draft)) {
          /*
            Both cells can be edited in one pass. The value is reported first
            only because it is the leftmost of the two — either way the row
            stays locked until an exposure is picked.
          */
          if (valueChanged) {
            setExposureRequired({ id: editingId, field: "asset_value" });
            return false;
          }
          if (debtChanged || pendingDebt?.id === editingId) {
            setExposureRequired({ id: editingId, field: "debt_value" });
            return false;
          }
        }
      }
      setExposureRequired(null);

      const patch = { ...draft };
      /*
        "Custom" on its own means nothing to the backend — it needs the name
        that goes with it. If the user picked Custom and clicked away without
        typing one, leave the exposure as it was rather than saving a blank.
      */
      if (
        patch.financial_exposure === FINANCIAL_EXPOSURE_CUSTOM &&
        String(patch.financial_exposure_custom_label || "").trim() === ""
      ) {
        delete patch.financial_exposure;
        delete patch.financial_exposure_custom_label;
      }

      /*
        Clearing the exposure is only allowed while the asset has no readings.
        Once a value has been recorded, that value is a valuation OF something,
        and dropping the exposure would leave the history describing nothing —
        so the blank is ignored and the stored exposure kept, matching the rule
        the Add / Edit Asset modal enforces.
      */
      const editedAsset = ((userData && userData.UserData) || {}).assets?.find(
        (a, i) => rowId(a, i) === editingId
      );
      const hasReadings =
        Array.isArray(editedAsset?.asset_evaluation_records) &&
        editedAsset.asset_evaluation_records.length > 0;
      if (
        hasReadings &&
        patch.financial_exposure !== undefined &&
        String(patch.financial_exposure).trim() === ""
      ) {
        delete patch.financial_exposure;
        delete patch.financial_exposure_custom_label;
      }
      /*
        Persist the row. Inline edits commit on click-away, so this fires once
        per row rather than per keystroke, and it goes through the same
        setUserData path every other save uses — there is no per-asset endpoint.

        The local override is cleared at the same time: once the real data
        carries the edit, keeping a shadow copy would leave the table reading
        from two sources that can disagree.
      */
      const saved = persistRowEdit(editingId, patch);
      if (saved) {
        setOverrides((prev) => {
          const next = { ...prev };
          delete next[editingId];
          return next;
        });
      } else {
        /*
          Could not match the row to an asset — keep it on screen rather than
          silently dropping what the user typed. Ownership staged in the modal
          goes in too: `overrides` knows how to merge it (see withOverrides),
          and it is about to be cleared with the rest of the edit state.
        */
        setOverrides((prev) => ({
          ...prev,
          [editingId]: {
            ...(prev[editingId] || {}),
            ...patch,
            ...(pendingOwnership && pendingOwnership.id === editingId
              ? {
                  my_ownership: pendingOwnership.my_ownership,
                  owners: pendingOwnership.owners,
                }
              : {}),
          },
        }));
      }
    }
    setEditingId(null);
    setDraft(null);
    setPendingOwnership(null);
    /*
      The held debt has been written by now. It must not survive into the next
      edit of this row: it is a copy of the asset as it stood before the commit,
      and using it as the baseline again would undo what was just saved.

      A refused commit returns above without reaching this, which is what keeps
      the debt alive while the row waits for its exposure.
    */
    setPendingDebt(null);
    return true;
  };

  const cancelEdit = () => {
    setEditingId(null);
    setDraft(null);
    setNameError(null);
    setExposureRequired(null);
    setPendingDebt(null);
    setPendingOwnership(null);
  };

  const beginEdit = (asset, index, columnKey) => {
    const id = rowId(asset, index);
    if (id === editingId || disabledEditing) return;
    /*
      This click closed another row. That is all it does; opening this one takes
      a second click.
    */
    if (closedByClickAwayRef.current) {
      closedByClickAwayRef.current = false;
      return;
    }
    /*
      Whichever row holds the lock has to let go first, and it is allowed to
      refuse — a clashing name, a figure with no exposure. Without honouring
      that, clicking a third row walked straight past the guard and threw the
      draft away.
    */
    if (!commitEdit()) return;
    /*
      Focus follows the cell that was clicked, not the start of the row.

      The Asset Name input used to carry `autoFocus`, so opening a row from any
      cell put the caret in the first column — and the browser scrolled that
      column into view, throwing away the horizontal scroll position the user
      was reading at. Now the field that is focused is the one under the
      pointer, and it is focused with `preventScroll` so even that cannot move
      the table sideways.
    */
    focusColumnRef.current = columnKey || null;
    setEditingId(id);
    setDraft({
      asset_name: asset.asset_name || "",
      asset_type: canonicalAssetType(asset.asset_type),
      asset_type_custom_label: "",
      financial_exposure: asset.financial_exposure || "",
      financial_exposure_custom_label: asset.financial_exposure_custom_label || "",
      is_liquid: asset.is_liquid === true,
      // Copied, not aliased: the modal edits the draft, never the saved asset.
      beneficiaries: [...(asset.beneficiaries || [])],
      trustees: [...(asset.trustees || [])],
      debt_type: asset.debt_type || "",
      debt_annual_interest: asset.debt_annual_interest || "",
      debt_payoff_date: asset.debt_payoff_date || "",
      asset_value:
        latestValueOf(asset) === "" ? "" : String(latestValueOf(asset)),
      // Already negative — `debtAmountOf` normalises the sign.
      debt_amount:
        debtAmountOf(asset) == null ? "" : String(debtAmountOf(asset)),
      /*
        The currency each amount will be recorded in. Seeded from what the cell
        already shows, so leaving the picker alone changes nothing.
      */
      custom: customFieldsOf(asset).reduce(
        (acc, f) => ({ ...acc, [String(f.title).trim()]: f.value }),
        {}
      ),
    });
  };

  const patchDraft = (patch) => setDraft((prev) => ({ ...prev, ...patch }));

  /*
    Clicking outside the locked row commits it — unless a modal is open. The
    ownership and debt modals render outside the row's DOM, so without this the
    first click inside either of them would read as "clicked away" and end the
    edit underneath. Escape and Enter are suppressed for the same reason: while
    a modal is up they belong to the modal, not to the row.
  */
  const modalOpen = Boolean(debtFor || ownershipFor || peopleFor);
  useEffect(() => {
    if (!editingId || modalOpen) return undefined;
    const onDown = (event) => {
      // A fresh press: whatever the last one left behind is stale.
      closedByClickAwayRef.current = false;
      /*
        A dropdown opened from this row renders its list in document.body, so by
        DOM position every option is outside the row. Picking one would commit
        the edit and close it — the opposite of what choosing a value should do.
        Treated as part of the row, so the edit stays open until the user really
        does click away.
      */
      if (
        event.target instanceof Element &&
        event.target.closest("[data-dropdown-menu]")
      ) {
        return;
      }
      if (editRowRef.current && !editRowRef.current.contains(event.target)) {
        /*
          Only when the row actually let go. A commit refused — a clashing name,
          a missing exposure — leaves the row open, and the click should not be
          swallowed on its behalf.
        */
        closedByClickAwayRef.current = commitEdit();
      }
    };
    const onKey = (event) => {
      if (event.key === "Escape") cancelEdit();
      if (event.key === "Enter") commitEdit();
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
    // `pendingDebt` is listed so the click-away handler re-registers around a
    // commitEdit that can see it, rather than holding the pre-modal closure.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editingId, draft, modalOpen, pendingDebt, pendingOwnership]);

  const fieldsRef = useRef(null);
  useEffect(() => {
    const onClickOutside = (event) => {
      if (fieldsRef.current && !fieldsRef.current.contains(event.target)) {
        setFieldsOpen(false);
      }
    };
    document.addEventListener("mousedown", onClickOutside);
    return () => document.removeEventListener("mousedown", onClickOutside);
  }, []);

  /*
    Custom-field columns are the union of every custom field title across the
    user's assets, so the table grows with whatever they have defined.
  */
  const customColumns = useMemo(() => {
    const titles = [];
    assets.forEach((asset) =>
      customFieldsOf(asset).forEach((field) => {
        const title = String(field.title).trim();
        if (!titles.includes(title)) titles.push(title);
      })
    );
    return titles.map((title) => ({ key: `custom:${title}`, label: title }));
  }, [assets]);

  const columns = useMemo(
    () => [
      // The row's own edge, before anything in the row.
      { ...EDGE_COLUMN, label: "" },
      // Touch-only, and first so it is reachable without scrolling sideways.
      ...(compact ? [{ ...REORDER_COLUMN, label: "" }] : []),
      ...BASE_COLUMNS.map((c) => ({ ...c, label: t(c.labelKey) })),
      ...customColumns,
      // Last, and never hideable.
      { ...ACTIONS_COLUMN, label: "" },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customColumns, content, compact]
  );

  /*
    The actions column is permanent: it is not offered in "Presented fields"
    and cannot be hidden, so there is always a way to open an asset.
  */
  /*
    The built-in types plus this user's own custom ones, so both the filter and
    the inline editor offer everything the Add / Edit Asset dropdown does. Built
    from labels because a custom type is stored as its label, not an id.
  */
  const assetTypeOptions = useMemo(() => {
    const custom = (
      (userData && userData.UserData &&
        userData.UserData.asset_type_custom_options) ||
      []
    ).map((option) => option.label);
    return Array.from(new Set([...ASSET_TYPES, ...custom])).filter(Boolean);
  }, [userData]);

  /*
    The type list for the row editor, as `Dropdown` wants it: one group with no
    heading, so it renders flat. Same contents and order the native <select>
    offered — the blank, this user's types among the built-ins, then Custom.
  */
  const assetTypeGroups = useMemo(
    () => [
      {
        label: "",
        options: [
          { value: "", label: EMPTY },
          ...assetTypeOptions.map((type) => ({
            value: type,
            label: getAssetTypeTranslated(
              type,
              filterContent,
              dublicatedcontent
            ),
          })),
          {
            value: ASSET_TYPE_CUSTOM,
            label: dublicatedcontent?.custom || "Custom",
          },
        ],
      },
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [assetTypeOptions, filterContent, dublicatedcontent]
  );

  const isVisible = (key) => {
    /*
      Permanent columns are never offered in "Presented fields", so no saved
      list can hide one -- including a list saved before a column became
      permanent, which is how an existing user could otherwise open the table
      with no Asset Name in it.
    */
    if (PERMANENT_KEYS.has(key)) return true;
    /*
      Never chosen -> the starting set, not the whole table. Sixteen columns and
      the user's own custom fields is more than fits on a screen, so the page
      opens on the ones that describe an asset and its money, and the rest are
      picked up from the menu.
    */
    if (!Array.isArray(presentedFields)) {
      return DEFAULT_PRESENTED_FIELDS.includes(key);
    }
    return presentedFields.includes(key);
  };
  /*
    Saves on every toggle. The first toggle turns the implicit "everything" into
    an explicit list, so the column just switched off is the only one missing —
    otherwise unticking one box would hide all the others too.

    The permanent action column is never listed: it cannot be hidden, and
    including it would make the saved list disagree with what is on screen.
  */
  const toggleColumn = (key) => {
    if (PERMANENT_KEYS.has(key)) return;
    const selectable = columns
      .filter((column) => !column.permanent && isVisible(column.key))
      .map((column) => column.key);
    const current = Array.isArray(presentedFields)
      ? presentedFields.filter((k) => k !== ACTIONS_COLUMN.key)
      : selectable;
    const next = current.includes(key)
      ? current.filter((k) => k !== key)
      : [...current, key];
    /*
      A partner's choice stops here: held for this view, kept for the session,
      and never written to the customer's account.
    */
    if (partnerReadOnly) {
      setPartnerFields(next);
      return;
    }
    const root = (userData && userData.UserData) || {};
    userData.setUserData({
      ...root,
      asset_management_presented_fields: next,
    });
    userData.Update(RandomId(10));
  };

  /*
    The exposure option labels live with the evaluation section, not with this
    page, so the dropdown here reads exactly the same strings as the one in the
    Add / Edit Asset modal.
  */
  const tEval = (key, fallback) => {
    const evaluationContent =
      websiteCurrentLang?.content?.User?.EditAsset?.AssetEvaluation;
    return (evaluationContent && evaluationContent[key]) || fallback;
  };

  /*
    This user's own saved exposure types, the same list the asset modal offers.
  */
  const customExposureOptions = useMemo(
    () =>
      ((userData && userData.UserData &&
        userData.UserData.financial_exposure_custom_options) ||
        []).filter((option) => option && option.value),
    [userData]
  );

  /*
    A custom exposure is stored as an ID (`fx_...`), unlike a custom asset type
    which is stored as its label — so it has to be looked up before it can be
    shown. Without that the cell printed the id itself.
  */
  const exposureLabel = (value) => {
    const option = FINANCIAL_EXPOSURE_OPTIONS.find((o) => o.value === value);
    if (option) return tEval(option.labelKey, value);
    const custom = customExposureOptions.find((o) => o.value === value);
    if (custom) return custom.label;
    return value || null;
  };

  /*
    The two filter lists, as MultiSelectFilter wants them.

    Neither carries a blank or a "Custom" entry the way the row editors do:
    those mean "choose one / name a new one", and neither is a thing an asset
    can BE, so neither is something to filter by.
  */
  const assetTypeFilterOptions = useMemo(
    () =>
      assetTypeOptions.map((type) => ({
        value: type,
        label: getAssetTypeTranslated(type, filterContent, dublicatedcontent),
      })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [assetTypeOptions, filterContent, dublicatedcontent]
  );

  const exposureFilterOptions = useMemo(
    () => [
      ...FINANCIAL_EXPOSURE_OPTIONS.map((option) => ({
        value: option.value,
        label: exposureLabel(option.value),
      })),
      /*
        And this user's own. Filtering by one was impossible before the list
        included them: the assets carried the id but the menu only offered the
        built-ins, so there was nothing to select.
      */
      ...customExposureOptions.map((option) => ({
        value: option.value,
        label: option.label,
      })),
    ],
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [customExposureOptions, filterContent, dublicatedcontent, websiteCurrentLang]
  );

  /*
    The exposure list for one row's editor, in the order the native <select>
    offered it. Built per row rather than memoised once, because whether the
    blank is on the list depends on that asset's own records.
  */
  const exposureOptionsFor = (asset, current) => {
    const options = [];
    /*
      Only offered while the asset has no readings. Once a value is recorded the
      exposure is what that value is a valuation OF, so clearing it is not
      offered rather than being offered and then refused.
    */
    const hasRecords =
      Array.isArray(asset?.asset_evaluation_records) &&
      asset.asset_evaluation_records.length > 0;
    if (!hasRecords) {
      options.push({
        value: "",
        label: tEval("assetEvaluationText16", "Select"),
      });
    }
    FINANCIAL_EXPOSURE_OPTIONS.forEach((option) =>
      options.push({ value: option.value, label: exposureLabel(option.value) })
    );
    /*
      This user's own saved types, the same list the asset modal and the filter
      offer. Without them the row editor could create a custom type but never
      pick one again — including the one the asset already held.
    */
    (customExposureOptions || []).forEach((option) =>
      options.push({ value: option.value, label: option.label })
    );
    /*
      A stored id in neither list. Listed so the field shows what the asset
      actually holds rather than falling back to whichever option comes first,
      which would rewrite it on the next commit untouched.
    */
    if (
      current &&
      current !== FINANCIAL_EXPOSURE_CUSTOM &&
      !options.some((option) => option.value === current)
    ) {
      options.push({ value: current, label: current });
    }
    options.push({
      value: FINANCIAL_EXPOSURE_CUSTOM,
      label: tEval("assetEvaluationText24", "Custom"),
    });
    return options;
  };

  /*
    A custom exposure is stored as financial_exposure = "custom" plus the name
    the user typed, so the cell shows that name rather than the sentinel.
  */
  /*
    A debt's description — its type, interest and payoff date — is asset-level,
    not part of the dated records. It only means anything once the asset has a
    debt, so the cells below show a dash and offer the debt modal until it does.
  */
  const hasDebt = (asset) =>
    Array.isArray(asset.asset_debt_records) &&
    asset.asset_debt_records.length > 0;

  // Translated label for a stored debt type, or "" when there is none.
  /*
    The currency an inline edit will actually be recorded in, shown as a fixed
    prefix on the two amount cells.

    Resolved exactly the way applyInlineValueEdit resolves it when it writes the
    record — the latest reading's currency, then the asset's, then the site
    language's default. Showing the language default unconditionally would be
    wrong for an asset whose history is in another currency: the cell already
    reads "22.22K CHF" there, and a "$" prefix over it would be a lie.

    Not editable: a reading's currency belongs to the reading, and changing it
    means changing what the existing history is denominated in. That is what the
    asset modal's picker is for.
  */
  const inlineCurrencyFor = (asset, isDebt) => {
    const resolved = isDebt
      ? latestDebtCurrencyOf(asset)
      : latestCurrencyOf(asset);
    return String(
      resolved || defaultCurrencyFor(websiteCurrentLang?.label)
    ).toLowerCase();
  };

  // The sign, for the picker. Falls back to the code for anything unmapped.
  const currencySign = (code) =>
    CURRENCY_SYMBOLS[String(code).toLowerCase()] || String(code).toUpperCase();

  const debtTypeLabel = (value) => {
    if (!value) return "";
    for (const group of DEBT_TYPE_GROUPS) {
      const match = group.options.find((option) => option.value === value);
      if (match) return tEval(match.labelKey, match.value);
    }
    return value;
  };

  /*
    Resolves the asset's beneficiary / trustee ids to the records that carry
    their names and avatars. Unresolvable ids are dropped rather than rendered:
    Beneficiarytrustee derives its initials from the name, and an empty name
    would put the string "undefined" in the circle.
  */
  const peopleOn = (asset, kind) => {
    const root = (userData && userData.UserData) || {};
    const source = root[kind] || [];
    return (asset[kind] || [])
      .map((user_id) => source.find((person) => person.user_id === user_id))
      .filter((person) => person && String(person.name || "").trim() !== "")
      .map((person) => ({
        user_id: person.user_id,
        name: [person.name, person.last_name].filter(Boolean).join(" "),
        priority: person.priority || 0,
        avatar: person.profile_avatar,
      }));
  };

  // The names in an asset's "other owners" block. Stored as plain names.
  const otherOwnersOf = (asset) => {
    const root = (userData && userData.UserData) || {};
    const entry = ownersEntry(asset);
    return ((entry && entry.owners) || [])
      // Rows written before owners were names still hold an id; show the name.
      .map((owner) => String(resolveOwnerName(owner.owner, root) || "").trim())
      .filter(Boolean);
  };

  const exposureCellLabel = (asset) => {
    if (asset.financial_exposure === FINANCIAL_EXPOSURE_CUSTOM) {
      const label = String(asset.financial_exposure_custom_label || "").trim();
      return label || null;
    }
    return exposureLabel(asset.financial_exposure);
  };

  const cellValue = (asset, key) => {
    if (key.startsWith("custom:")) {
      const title = key.slice("custom:".length);
      const match = customFieldsOf(asset).find(
        (f) => String(f.title).trim() === title
      );
      return match && String(match.value).trim() !== "" ? match.value : null;
    }
    switch (key) {
      case "asset_name":
        return asset.asset_name || null;
      case "asset_type":
        return asset.asset_type || null;
      case "financial_exposure":
        return exposureCellLabel(asset);
      case "asset_value":
        // The most recently added valuation, not the asset-level scalar.
        return formatValue(latestValueOf(asset), latestCurrencyOf(asset));
      case "debt_value": {
        const debt = debtAmountOf(asset);
        return debt === null
          ? null
          : formatValue(debt, latestDebtCurrencyOf(asset));
      }
      case "my_ownership": {
        /*
          With nobody else on the asset it is wholly the user's, so the column
          says so rather than leaving a dash the user has to interpret. The
          modal will not save a split that does not total 100%, so a stored
          share below it cannot coexist with an empty owners list.
        */
        if (otherOwnersOf(asset).length === 0) return "100%";
        const entry = ownersEntry(asset);
        return entry && entry.my_ownership !== ""
          ? `${entry.my_ownership}%`
          : null;
      }
      case "is_liquid":
        /*
          True or false on the customer's own account -- the switch in the modal
          always holds one of the two.

          Not so in a partner's view: an asset whose evaluations were not shared
          arrives without the field at all, and reading that as "No" would tell
          them it is not liquid, which the customer never said. Absent is blank.
        */
        if (typeof asset.is_liquid !== "boolean") return null;
        return asset.is_liquid === true
          ? tEval("assetEvaluationText18", "Yes")
          : tEval("assetEvaluationText17", "No");
      case "debt_type":
        return hasDebt(asset) ? debtTypeLabel(asset.debt_type) || null : null;
      case "debt_annual_interest": {
        if (!hasDebt(asset)) return null;
        const interest = String(asset.debt_annual_interest || "").trim();
        return interest === "" ? null : `${interest}%`;
      }
      case "debt_payoff_date":
        return hasDebt(asset)
          ? String(asset.debt_payoff_date || "").trim() || null
          : null;
      /*
        Blank on an asset that predates the field, rather than a date nobody
        set. It fills in the first time the asset is touched.
      */
      case "last_updated":
        return formatStoredDate(asset.last_updated, websiteCurrentLang?.label) || null;
      case "other_owners": {
        const names = otherOwnersOf(asset);
        return names.length ? names.join(", ") : null;
      }
      /*
        Sort and filter see the names; the cells render avatars. Returning the
        names rather than a count is what lets the column sort alphabetically
        by whoever is first on the asset.
      */
      case "beneficiaries":
      case "trustees": {
        // The column keys are the asset's own field names.
        const people = peopleOn(asset, key);
        return people.length ? people.map((p) => p.name).join(", ") : null;
      }
      default:
        return null;
    }
  };

  /*
    What a cell actually READS, for the search below.

    `cellValue` is what the cells are built from and is the same text in all
    but one place: the asset type cell translates what it is given, so an
    asset stored as `online_storage` shows "Online Storage" -- and in French
    something else again. Searching the stored spelling would miss the words
    in front of the reader and match ones that are nowhere on screen.
  */
  const cellText = (asset, key) => {
    const value = cellValue(asset, key);
    if (key !== "asset_type" || !value) return value;
    return getAssetTypeTranslated(value, filterContent, dublicatedcontent);
  };

  /*
    Which columns are on screen. Declared here rather than beside the column
    widths below, because the search reads it: what the table is showing is
    what the search looks through.
  */
  const visibleColumns = columns.filter((c) => isVisible(c.key));
  const visibleKeys = visibleColumns.map((column) => column.key).join("|");

  /*
    Search matches anything the table is SHOWING -- the name, the type, the
    exposure, a figure, a date, a person in one of the people columns. So "te"
    finds an asset named "Tesla", one typed "Real estate", and one whose
    advisor is "Peter". Case-insensitive, and not anchored to the start.

    It used to read the name and nothing else, which made it look broken
    rather than narrow: the reader can see "Real estate" in the row in front
    of them, types it, and the row disappears.

    Read through `cellValue`, which is what the cells themselves are built
    from -- so what is searched is exactly what is on screen: "$2.01K", "100%",
    "Yes", the date in the reader's own format. Never the raw figures behind
    them, which are not what anybody is reading.

    Only the VISIBLE columns, so switching a column off stops it matching.
    A row that appears for no visible reason is worse than one that does not
    appear.
  */
  const rows = useMemo(() => {
    const needle = search.trim().toLowerCase();
    const matchesSearch = (asset) =>
      !needle ||
      visibleColumns.some((column) => {
        const value = cellText(asset, column.key);
        return value != null && String(value).toLowerCase().includes(needle);
      });
    let list = assets.filter((asset) => {
      if (!matchesSearch(asset)) return false;
      /*
        Compared canonically so an asset stored under a legacy spelling still
        matches its type in the filter — otherwise filtering by "Online storage"
        would silently hide the ones saved as "online_storage".
      */
      if (!matchesSelection(canonicalAssetType(asset.asset_type), assetTypes))
        return false;
      if (!matchesSelection(asset.financial_exposure, exposures)) return false;
      return true;
    });

    if (!sort.key) {
      list = sortByStoredOrder(list, ORDER_FIELDS.management);
    }

    if (sort.key) {
      list = [...list].sort((a, b) => {
        if (sort.key === "asset_value" || sort.key === "debt_value") {
          const read = (asset) =>
            sort.key === "debt_value"
              ? debtAmountOf(asset)
              // Sort on the same figure the column displays: the latest record.
              : latestValueOf(asset) === ""
              ? null
              : Number(latestValueOf(asset));
          const norm = (v) => (v == null || Number.isNaN(v) ? -Infinity : v);
          const an = norm(read(a));
          const bn = norm(read(b));
          return sort.desc ? bn - an : an - bn;
        }
        /*
          Counts and the interest rate are numbers wearing a string; comparing
          them as text would put "10" before "3". Empty sorts last ascending,
          the same way a missing value does above.
        */
        if (NUMERIC_COLUMNS.has(sort.key)) {
          const read = (asset) => {
            const value = parseFloat(
              String(cellValue(asset, sort.key) || "").replace("%", "")
            );
            return Number.isNaN(value) ? -Infinity : value;
          };
          return sort.desc ? read(b) - read(a) : read(a) - read(b);
        }
        if (RAW_SORT_COLUMNS.has(sort.key)) {
          const ad = String(a[sort.key] || "");
          const bd = String(b[sort.key] || "");
          return sort.desc ? bd.localeCompare(ad) : ad.localeCompare(bd);
        }
        const as = String(cellValue(a, sort.key) || "").toLowerCase();
        const bs = String(cellValue(b, sort.key) || "").toLowerCase();
        return sort.desc ? bs.localeCompare(as) : as.localeCompare(bs);
      });
    }
    return list;
    /*
      `visibleKeys`, not `visibleColumns`: that array is rebuilt on every
      render, and depending on it would recompute this filter and sort every
      time anything on the page changed. The keys are what the search actually
      cares about, and they only change when a column is switched on or off.
    */
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [assets, search, visibleKeys, assetTypes, exposures, sort]);

  /*
    Only the shortfall, so a table with ten rows or more is not padded at all.
    An empty table still draws its "nothing here" row, which counts as one.
  */
  const floorHeight = Math.max(
    0,
    (FLOOR_ROWS - Math.max(rows.length, 1)) * ROW_SLOT
  );

  /*
    Drops the dragged row at the target's position. Computed against the full
    asset list, not the filtered view, so the saved order stays meaningful when
    a filter is cleared.
  */
  /*
    Writes the new order onto the assets themselves and saves. Computed against
    the full asset list rather than the filtered view, so the order still makes
    sense once filters are cleared — and reorderAssets rewrites every row's
    position, which is also what backfills assets that never had one.
  */
  /*
    One place up or down, for the step buttons. Same rules as a drag: the manual
    order becomes the active one, because a live column sort would undo the move
    on the next render.
  */
  /*
    Dropping one row onto another. The drop lands the row BEFORE its target,
    which is what the native drag's insertion line has been showing.
  */
  const moveRow = (fromId, toId) => {
    if (!fromId || !toId || fromId === toId) return;
    const root = (userData && userData.UserData) || {};
    const next = reorderAssets(
      root.assets || [],
      ORDER_FIELDS.management,
      fromId,
      toId
    );
    if (next === (root.assets || [])) return;
    // Manual order wins; a live column sort would immediately undo the move.
    setSort({ key: null, desc: false });
    userData.setUserData({ ...root, assets: next });
    userData.Update(RandomId(10));
  };

  /*
    Where the gap belongs for a given pointer position: above the first row
    whose middle is below the cursor, or after the last row if none is.

    The same rule the reference snippet uses, and read off the DOM rather than
    from React's list because the gap row sits among the rows and shifts them --
    positions have to come from where things actually are.

    On the tbody rather than on each row: a release over the GAP lands on no row
    at all, so a per-row handler simply never fired and the drop was lost.
  */
  /*
    Where the gap sat when the drag began -- the dragged row's own place. That
    one gap must appear at full size immediately; see .gap_row_resting.
  */
  const restingGapRef = useRef(null);

  /*
    The two ends of a touch drag, as indexes into the rendered list: the row in
    hand and the row whose place it is over. Everything between them shifts by
    one slot, in the direction that opens the space.

    Computed once per render rather than per row -- finding both ends is a walk
    of the list, and doing it inside the row map would make it a walk per row.
  */
  const touchDrag = useMemo(() => {
    if (!compact || !dragId) return null;
    const indexOf = (id) => rows.findIndex((asset, i) => rowId(asset, i) === id);
    const from = indexOf(dragId);
    if (from < 0) return null;
    const to = indexOf(dragOverId);
    return { from, to: to < 0 ? from : to };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [compact, dragId, dragOverId, rows]);

  /*
    Where a row sits while a finger is dragging. `undefined` for every row not
    involved, so nothing is written onto rows that are not moving.
  */
  const touchRowStyle = (id, index) => {
    if (!touchDrag) return undefined;
    // The row in hand: straight under the finger, no easing to lag behind it.
    if (id === dragId) return { transform: `translateY(${handleDragY}px)` };
    const { from, to } = touchDrag;
    if (from < index && index <= to)
      return { transform: `translateY(${-ROW_SLOT}px)` };
    if (to <= index && index < from)
      return { transform: `translateY(${ROW_SLOT}px)` };
    return undefined;
  };

  const gapTargetAt = (body, clientY) =>
    gapTargetFor(
      Array.from(body.querySelectorAll("tr[data-row-id]"))
        .filter((el) => el.dataset.rowId !== dragId)
        .map((el) => {
          const box = el.getBoundingClientRect();
          return { id: el.dataset.rowId, top: box.top, height: box.height };
        }),
      clientY,
      DROP_AT_END
    );

  const stepRow = (id, delta) => {
    const root = (userData && userData.UserData) || {};
    const next = moveAssetBy(
      root.assets || [],
      ORDER_FIELDS.management,
      id,
      delta
    );
    if (next === (root.assets || [])) return;
    setSort({ key: null, desc: false });
    userData.setUserData({ ...root, assets: next });
    userData.Update(RandomId(10));
  };

  /*
    ---- dragging by the grip (touch) ----

    Pointer events rather than HTML5 drag, because that is the only drag a
    finger can start. Three things have to be arranged by hand that the native
    drag would have done:

    - The pointer is captured on the grip, so the move and release keep arriving
      there even once the finger has left the little square it started in.
    - The row under the finger is found by hit-testing the page, since a
      captured pointer sends no events to the rows it passes over.
    - The page will not scroll itself while the grip owns the gesture, so
      holding near the top or bottom edge scrolls it instead.
  */
  const dragScrollRef = useRef(0);
  const dragFrameRef = useRef(null);
  const stopDragScroll = () => {
    dragScrollRef.current = 0;
    if (dragFrameRef.current !== null) {
      window.cancelAnimationFrame(dragFrameRef.current);
      dragFrameRef.current = null;
    }
  };
  const runDragScroll = () => {
    if (!dragScrollRef.current) {
      dragFrameRef.current = null;
      return;
    }
    window.scrollBy(0, dragScrollRef.current * 10);
    dragFrameRef.current = window.requestAnimationFrame(runDragScroll);
  };
  const setDragScroll = (direction) => {
    if (direction === dragScrollRef.current) return;
    dragScrollRef.current = direction;
    if (direction && dragFrameRef.current === null) {
      dragFrameRef.current = window.requestAnimationFrame(runDragScroll);
    }
  };
  // Undone if the component goes away mid-drag.
  useEffect(() => stopDragScroll, []);

  /*
    ---- reordering by the grip, on a phone ----

    A finger drag has no drag image. The desktop gesture can close the row to
    nothing because the browser is already carrying a photograph of it under the
    cursor; here there is nothing carrying it, so closing the row just makes it
    vanish out of the reader's hand -- which is what it did.

    So on touch the row is not closed. It stays whole and rides with the finger,
    and the rows it passes slide out of its way by exactly one slot. All of that
    is `transform`, which paints somewhere new without occupying anywhere new:
    the table's height never changes, so nothing below it moves either.
  */
  // Where the rows were when the drag began, and how far the finger has come.
  const handleRowsRef = useRef([]);
  const handleStartYRef = useRef(0);
  const [handleDragY, setHandleDragY] = useState(0);

  const startHandleDrag = (event, id) => {
    if (disabledEditing) return;
    // Stops the press selecting text or reaching the cell as a click.
    event.preventDefault();
    event.stopPropagation();
    if (event.currentTarget.setPointerCapture) {
      event.currentTarget.setPointerCapture(event.pointerId);
    }
    commitEdit();
    /*
      Measured now, before anything has moved, and kept for the whole gesture --
      see rowUnder for why reading live positions makes the list shiver.
    */
    const body = bodyScrollRef.current;
    handleRowsRef.current = body
      ? Array.from(body.querySelectorAll("tr[data-row-id]")).map((el) => {
          const box = el.getBoundingClientRect();
          return {
            id: el.dataset.rowId,
            top: box.top + window.scrollY,
            height: box.height,
          };
        })
      : [];
    handleStartYRef.current = event.clientY;
    setHandleDragY(0);
    setDragId(id);
    setDragOverId(id);
  };

  const moveHandleDrag = (event) => {
    if (!dragId) return;
    event.preventDefault();
    setHandleDragY(event.clientY - handleStartYRef.current);
    const target = rowUnder(
      handleRowsRef.current,
      event.clientY + window.scrollY
    );
    if (target) setDragOverId(target);
    // Within this much of an edge, the page follows the finger.
    const EDGE = 72;
    if (event.clientY < EDGE) setDragScroll(-1);
    else if (event.clientY > window.innerHeight - EDGE) setDragScroll(1);
    else setDragScroll(0);
  };

  const endHandleDrag = () => {
    stopDragScroll();
    setHandleDragY(0);
    handleRowsRef.current = [];
    /*
      Moved by however many places the finger travelled. Expressed as a step
      rather than a drop because a drop lands BEFORE its target, which for a row
      dragged downwards is the position it just left — see moveAssetBy.
    */
    if (dragId && dragOverId && dragId !== dragOverId) {
      const from = rows.findIndex((asset, i) => rowId(asset, i) === dragId);
      const to = rows.findIndex((asset, i) => rowId(asset, i) === dragOverId);
      if (from >= 0 && to >= 0) stepRow(dragId, to - from);
    }
    setDragId(null);
    setDragOverId(null);
  };


  /*
    Three states per column, cycled by clicking its header: ascending,
    descending, then off again. Without that third click there was no way out
    of a sort at all — the only thing that cleared one was dragging a row,
    which nobody would guess at.

    Clearing back to `key: null` is what hands the list to the manual order
    below, so the third click returns the exact view the table opens on, and
    the header icon falls back to its neutral up-down arrow on its own.

    Moving to a different column always starts that column ascending rather
    than inheriting the previous one's direction.
  */
  const toggleSort = (key) =>
    setSort((prev) => {
      if (prev.key !== key) return { key, desc: false };
      if (!prev.desc) return { key, desc: true };
      return { key: null, desc: false };
    });

  /* Locally-held edits merged over the source data. */
  const withOverrides = (asset, index) => {
    const patch = overrides[rowId(asset, index)];
    if (!patch) return asset;
    const merged = { ...asset };
    if (patch.asset_name !== undefined) merged.asset_name = patch.asset_name;
    if (patch.asset_type !== undefined) merged.asset_type = patch.asset_type;
    if (patch.financial_exposure !== undefined)
      merged.financial_exposure = patch.financial_exposure;
    if (patch.financial_exposure_custom_label !== undefined)
      merged.financial_exposure_custom_label =
        patch.financial_exposure_custom_label;
    if (patch.asset_value !== undefined) merged.asset_value = patch.asset_value;
    if (patch.debt_amount !== undefined) {
      merged.debt_amount = patch.debt_amount;
      merged.has_associated_debt = String(patch.debt_amount).trim() !== "";
    }
    if (
      patch.cleared ||
      patch.my_ownership !== undefined ||
      patch.owners !== undefined
    ) {
      /*
        The modal can add owners to an asset that never had an owners block, so
        one is created rather than dropping the edit.
      */
      merged.data_entries = mergeOwnership(
        merged,
        patch,
        `entry_local_${rowId(asset, index)}`
      ).data_entries;
    }
    if (patch.custom) {
      merged.data_entries = (merged.data_entries || []).map((e) =>
        e.type === "custom"
          ? {
              ...e,
              custom_fields: (e.custom_fields || []).map((f) =>
                patch.custom[String(f.title).trim()] !== undefined
                  ? { ...f, value: patch.custom[String(f.title).trim()] }
                  : f
              ),
            }
          : e
      );
    }
    return merged;
  };

  /*
    ---- column widths ----

    Every column is sized to the widest thing in it — its own heading, or the
    longest value any row shows — instead of splitting the table evenly.

    `table-layout: fixed` gives a column exactly the width it is told and lets
    the text spill out of it, which is what put "Investment Property Mortgage"
    on top of the Annual Interest figures. Measuring the content first is what
    makes the given width the right one; `.table td` also clips to its cell now,
    so an underestimate ends in an ellipsis rather than on the neighbour.

    Widths are estimated from character counts rather than measured in the DOM.
    A measuring pass would be exact, but it means rendering, reading back, and
    re-rendering on every data change; the estimate is stable, cheap, and only
    has to be close, since the floor and ceiling below absorb the error.
  */
  const columnWidths = useMemo(() => {
    // Roughly the advance width of the table's 14px face, bold for headings.
    const BODY_CHAR = 8.5;
    const PADDING = 32; // 15px each side, plus a little air
    /*
      The sort affordance: the 16px arrow plus the 12px `.th_sortable
      .th_label` margin in front of it. It was 24, two short of the 28 it
      actually takes — and since `.table th` is nowrap with an ellipsis, two
      pixels was enough to put a "..." after a heading that fits perfectly.
    */
    const SORT_ICON = 28;
    const NAME_EXTRAS = 70; // the drag handle and the asset-type logo (30px)
    const MAX = 300;
    /*
      Floors, because a cell has to hold its editor as well as its text: a date
      input and a debt-type dropdown are far wider than the values they show.
    */
    const FLOOR = {
      asset_name: 190,
      asset_type: 160,
      financial_exposure: 160,
      /*
        These two are sized by their heading, not by this floor — "Asset Value"
        measures 145px with its sort arrow, and the values themselves need
        about 112. The floor only has to keep the inline editor usable.

        Measured: the editor wants 123px for "€1,250,000" and 172px for a long
        CHF figure, so it has always scrolled for the widest case and still
        does. 120 keeps the common case comfortable without the floor becoming
        the thing that widens the column, which is what 152 had become.
      */
      asset_value: 120,
      debt_value: 120,
      my_ownership: 130,
      is_liquid: 100,
      debt_type: 200,
      // Room for the "%" that trails the field in edit mode.
      debt_annual_interest: 156,
      debt_payoff_date: 165,
      // Read-only, so no editor to fit -- just the YYYY/MM/DD it shows.
      last_updated: 140,
      other_owners: 150,
      /*
        Enough for three avatars and the "+N", and nothing beyond it. The old
        140/160 were sized to fit the headings, which now wrap instead.
      */
      beneficiaries: 96,
      trustees: 96,
    };
    return visibleColumns.map((column) => {
      // Exactly the corner radius: see EDGE_COLUMN.
      if (column.key === EDGE_COLUMN.key) return 20;
      // Two stacked chevrons need less room than the edit icon's cell.
      if (column.key === REORDER_COLUMN.key) return 44;
      /*
        32px of icon plus the cell's own 15px either side. Keyed on the actions
        column itself, not on `permanent` -- that flag now also marks the asset
        name, which is a text column and must keep its measured width.
      */
      if (column.key === ACTIONS_COLUMN.key) return 64;
      /*
        The heading measured for real, not estimated per character.

        The old per-character estimate assumed every character was the same
        width, and on these labels it overshot badly — "Asset Value" by 13px,
        and French "Valeur de la dette" by 34. Since a heading is what sets the width of
        any column whose values are shorter than its title, that overshoot was
        empty space in the Asset Value and Debt Value columns on every row.

        The two people columns already measured theirs for exactly this
        reason. This is the same fix applied to the rest.
      */
      let width =
        measureHeading(column.label) +
        PADDING +
        (column.sortable ? SORT_ICON : 0);
      if (column.key === "asset_name") width += NAME_EXTRAS;
      /*
        The two people columns draw avatars, not text, so their content width
        comes from how many are on the widest row — three at 32px overlapping
        by 7, then the "+N".

        Each avatar renders 34px wide, not 32: `.people_cell > *` rings it in
        1.5px of white on `box-sizing: content-box`. So n of them span
        n*34 - (n-1)*7, i.e. n*27 + 7. The old constant was the bare 26-7 and
        left the ring out, running 2px short per avatar.

        Sized by their content, or by their heading on ONE line — whichever
        is wider — and by nothing else.

        The character-count estimate is dropped here in favour of measuring
        the heading for real. It overshoots by 15-29px on these labels, and
        since the heading is what decides these two columns, that overshoot
        was landing directly beside the avatars as empty space:

            Trusted Advisors          162px estimated -> 126px of text
            Conseillers de confiance  226px estimated -> 177px of text

        No allowance for `.th_label`'s right margin any more: that margin now
        belongs to `.th_sortable .th_label`, and neither of these columns is
        sortable, so the heading occupies exactly what it measures. That is the
        12px this column used to hold open for nothing.

        What is left is the true minimum. "Trusted Advisors" measures 129px,
        so the column is 161px against the 154px the avatars need -- 7px wider
        than the content, which is the cost of the heading on one line.
      */
      if (column.key === "beneficiaries" || column.key === "trustees") {
        const most = rows.reduce(
          (max, asset) => Math.max(max, (asset[column.key] || []).length),
          0
        );
        const avatars =
          Math.min(most, 3) * 27 + 7 + (most > 3 ? 34 : 0) + PADDING;
        width = Math.max(avatars, measureHeading(column.label) + PADDING);
      } else {
        for (const asset of rows) {
          const text = String(cellValue(asset, column.key) ?? "");
          width = Math.max(width, text.length * BODY_CHAR + PADDING);
        }
      }
      // Ceil, not round: rounding down lands a pixel inside the text it measured.
      return Math.ceil(
        Math.min(MAX, Math.max(FLOOR[column.key] || 110, width))
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleColumns, rows, websiteCurrentLang]);

  /*
    Both tables get the same total or the header stops lining up with the body.
    Left as a min-width rather than a width: when the columns need less than the
    card, they share out the slack instead of leaving it blank at the edge.
  */
  const tableMinWidth = `${columnWidths.reduce((sum, w) => sum + w, 0)}px`;

  /*
    The header and the rows are two separate tables so the card can end below
    the column names, with the rows as their own cards. `table-layout: fixed`
    plus this shared colgroup keeps their columns identical, and the two scroll
    containers are kept in step below.
  */
  /*
    Widths live here and nowhere else. Both the header and the body are separate
    <table>s sharing this colgroup, and under `table-layout: fixed` a width set
    on a cell in one of them (rather than here) constrains that table alone —
    the other then divides the remaining space differently and every column
    drifts a little further out of line than the last.
  */
  const colGroup = (
    <colgroup>
      {visibleColumns.map((column, index) => (
        /*
          Asset Name is left without a width, and that is what pins every other
          column to the size computed for it.

          Under `table-layout: fixed` a table wider than its columns hands the
          slack out in proportion to their widths, so on a 1200px card a 173px
          Trusted Advisors column rendered at 289 -- the column was sized to its
          three avatars and then quietly grew by two thirds again. Leaving one
          column auto sends the whole slack there instead: the others render at
          exactly their computed width, and the name -- which truncates, and is
          the column a reader wants the room in -- takes what is left.

          Safe to key on asset_name because it is in PERMANENT_KEYS: it cannot
          be switched off, so there is always exactly one auto column. Its own
          computed width still counts towards the table's min-width, so nothing
          changes once the card is narrow enough to scroll sideways.
        */
        <col
          key={column.key}
          style={
            column.key === "asset_name"
              ? undefined
              : { width: `${columnWidths[index]}px` }
          }
        />
      ))}
    </colgroup>
  );

  /*
    ---- drag sideways to scroll ----

    The table is wider than the card, so dragging it sideways scrolls it.
    Dragging a row up or down still reorders it, exactly as before — that is a
    native HTML5 drag and is left completely alone.

    Telling the two apart is the whole difficulty, and it is decided in the two
    or three pointer moves before the browser opens its own drag — see
    panGesture.js, which has the measurements. Sideways claims the press and
    cancels the drag; anything else falls through and the row is picked up with
    the browser's own drag exactly as it always was.

    It is NOT decided at `dragstart`, which is where it used to be attempted:
    that event reports the position the press went down at, so the travel read
    from it is always zero and it can say nothing about direction.

    The pan then runs off document listeners rather than React's, because the
    press it inherits is mid-gesture and the element under it is about to move.

    Mouse only. Touch pans natively with momentum this cannot match, and a
    finger reorders by the grip (see REORDER_COLUMN) rather than by the row.
  */
  /*
    How far the table has to be dragged before the press stops counting as a
    click on the cell underneath.
  */
  const PAN_CLICK_TRAVEL = 4;
  const panStartRef = useRef(null);
  const panRef = useRef(null);
  /*
    Where the pointer has actually reached.

    `dragstart` reports the position the press went DOWN at, so there is no
    travel to read off it -- but by then we have seen the moves, and this is
    them. It is what lets the decision be made after the browser has already
    made its own.
  */
  const panLastRef = useRef(null);
  // A pan ends in a click the cell would otherwise open edit mode on.
  const panSuppressClickRef = useRef(false);
  const [panning, setPanning] = useState(false);

  /*
    Presses that belong to something else: a field being typed in, a button, an
    option in an open dropdown. Panning from those would swallow the interaction.
  */
  const panIgnores = (target) =>
    target instanceof Element &&
    target.closest(
      "input, textarea, select, button, a, [role='option'], [data-dropdown-menu]"
    );

  // Where a press went down, kept until it is claimed by one gesture or ends.
  const onPanPointerDown = (event) => {
    if (event.pointerType !== "mouse" || event.button !== 0) return;
    if (panIgnores(event.target)) return;
    /*
      A press on the scroller's own scrollbar belongs to the browser.

      A scrollbar has no element of its own, so the press arrives with
      `target` set to the scroller and walks straight past panIgnores above.
      The pan then claimed a gesture the browser was already handling, and the
      two drove scrollLeft at once from the same mouse: the scrollbar moves the
      content by thumb-distance x scrollWidth/clientWidth, the pan moves it
      pixel for pixel. Two different answers written in the same frame, which
      is what made dragging the bar judder -- never a dropped frame, just the
      content thrown between two positions.

      `clientHeight` stops above the horizontal scrollbar while the bounding
      rect includes it, so anything below that line is the bar. The same test
      on the other axis costs nothing and keeps this right if the table is ever
      given a vertical scrollbar.
    */
    const scroller = event.currentTarget;
    if (scroller instanceof Element) {
      const rect = scroller.getBoundingClientRect();
      if (event.clientY - rect.top > scroller.clientHeight) return;
      if (event.clientX - rect.left > scroller.clientWidth) return;
    }
    panStartRef.current = { x: event.clientX, y: event.clientY };
    /*
      No text selection for the life of this press.

      `.panning` already says this, and says it too late: it lands when the
      pan claims at three pixels, and Chrome does not cancel a selection that
      has already begun -- the first move started one. A CUSTOMER never saw it
      because their rows are `draggable`, and a draggable element suppresses
      selection by itself; a PARTNER's rows are not (disabledEditing), so the
      same drag highlighted its way across the table while it scrolled. That
      is the whole of the difference between the two accounts.

      Written to the node rather than through state, like holdTableHeight: it
      has to be in effect for the very next move, not after a render.
    */
    scroller.classList.add(styles.pressing);
    /*
      Cleared with it. Otherwise a press whose dragstart arrives before any
      move of its own would be judged against where the LAST press ended --
      a travel this one never made, and usually a large sideways one.
    */
    panLastRef.current = null;
  };

  /*
    The teardown for a pan in progress, held in a ref.

    It has to be the very function that was registered, and both it and the move
    handler are rebuilt on every render — starting a pan sets state, so a render
    happens immediately after they are added. Removing "the current one" would
    therefore be removing a different function object than the one listening,
    and the pan would carry on after the mouse came up. Keeping the pair the
    press actually installed is what makes the removal match.
  */
  const panTeardownRef = useRef(null);
  const stopPan = () => {
    if (panTeardownRef.current) panTeardownRef.current();
  };

  /*
    Called from a pointermove anywhere on the table. Returns true when it has
    taken the press over, which is the caller's cue to cancel whatever it was
    about to do.
  */
  const beginPanAt = (x, y, minTravel) => {
    const from = panStartRef.current;
    if (!from || panRef.current) return false;
    const gesture = gestureFrom(x - from.x, y - from.y, minTravel);
    if (gesture === "undecided") return false;
    if (gesture === "drag") {
      // Upwards or downwards: not ours. Let the row be dragged.
      panStartRef.current = null;
      return false;
    }
    const el = bodyScrollRef.current;
    // Nothing to scroll to: leave the press alone.
    if (!el || el.scrollWidth <= el.clientWidth) {
      panStartRef.current = null;
      return false;
    }
    panRef.current = { x: from.x, scrollLeft: el.scrollLeft };
    setPanning(true);
    /*
      Not suppressed yet. A pan can now be claimed on three pixels, and three
      pixels of wobble is an ordinary part of clicking — so the click is only
      taken away once the table has actually been scrolled by it. Below that
      nothing moved on screen, and the press should still open the cell.
    */

    const onMove = (moveEvent) => {
      const pan = panRef.current;
      if (!pan) return;
      // Released while outside the window: the mouseup never reached us.
      if (moveEvent.buttons === 0) {
        onUp();
        return;
      }
      moveEvent.preventDefault();
      const scroller = bodyScrollRef.current;
      if (scroller) {
        scroller.scrollLeft = pan.scrollLeft - (moveEvent.clientX - pan.x);
      }
      // Far enough that the reader saw the table move, not a wobble.
      if (Math.abs(moveEvent.clientX - pan.x) > PAN_CLICK_TRAVEL) {
        panSuppressClickRef.current = true;
      }
    };
    function onUp() {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      panTeardownRef.current = null;
      panRef.current = null;
      panStartRef.current = null;
      panLastRef.current = null;
      const scroller = bodyScrollRef.current;
      if (scroller) scroller.classList.remove(styles.pressing);
      setPanning(false);
    }

    panTeardownRef.current = onUp;
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return true;
  };

  const beginPanIfSideways = (event) =>
    beginPanAt(event.clientX, event.clientY);

  // Surfaces with no native drag of their own reach the same decision here.
  const onPanPointerMove = (event) => {
    if (event.pointerType !== "mouse" || panRef.current) return;
    if (panStartRef.current) {
      panLastRef.current = { x: event.clientX, y: event.clientY };
    }
    beginPanIfSideways(event);
  };

  /*
    Undone if the table goes away mid-press. `stopPan` is rebuilt every render
    and the listeners it removes are the ones the current one added, so the
    empty list is deliberate: this is an unmount cleanup, not a subscription.
  */
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => stopPan, []);

  /*
    The click that ends a pan would otherwise land on whatever cell the pointer
    finished over and open it for editing. Caught on the way down, before any
    cell sees it.
  */
  const onPanClickCapture = (event) => {
    if (!panSuppressClickRef.current) return;
    panSuppressClickRef.current = false;
    event.stopPropagation();
    event.preventDefault();
  };

  const panHandlers = {
    onPointerDown: onPanPointerDown,
    onPointerMove: onPanPointerMove,
    onPointerUp: (event) => {
      if (!panRef.current) panStartRef.current = null;
      if (event.currentTarget instanceof Element) {
        event.currentTarget.classList.remove(styles.pressing);
      }
    },
    onClickCapture: onPanClickCapture,
  };

  const bodyScrollRef = useRef(null);

  /*
    The table's height is held for the length of a drag, and let go on the drop.

    A gap is mounted when it appears and unmounted when it moves, so it opens
    over 0.18s but shuts instantly: for that 0.18s the outgoing gap is already
    gone and the incoming one has not finished arriving, and the table stands a
    row short. Inside the table that is invisible -- it is the animation. Below
    it, everything moves: the footer rose and settled on every pass of the
    pointer across a row.

    Holding the height asserts nothing untrue. A row leaves the flow and a gap
    of exactly its slot takes its place, so the height at rest is also the
    height at the end; the only moments it differs are the ones mid-animation.
    This states the settled value throughout and lets the interior animate
    underneath it.

    Read off the table rather than off the scroller: `min-height` sizes the
    content box, and .body_scroll's is its padding shorter than the box the
    scroller measures.

    Written to the node instead of through state -- it has to land in the frame
    the drag starts, and it changes nothing React renders.
  */
  const holdTableHeight = () => {
    const el = bodyScrollRef.current;
    const table = el && el.querySelector("table");
    if (table) el.style.minHeight = `${table.offsetHeight}px`;
  };

  const releaseTableHeight = () => {
    const el = bodyScrollRef.current;
    if (el) el.style.minHeight = "";
  };

  /*
    ---- where the rows get clipped ----

    The rows scroll in a container held 16px wider than the card on each side
    (see .body_scroll): a row's drop shadow is painted outside the row, and
    `overflow-x: auto` clips at the padding box, so without that room the
    shadow is sliced off at the row's rounded ends.

    The cost is that scrolled-away content shows in those same 16px, 16px past
    the card the column headings sit in -- the header clips at the card, the
    rows 16px outside it, and the two stop lining up.

    The right strip is clipped for as long as the table can scroll at all,
    whatever the scroll position -- not only while there is something still to
    the right, as it was. What that strip used to show at the far end was the
    row ends' own shadow, and the pinned edit cell carries that shadow itself
    now (see .cell_pinned_right), so the only thing left out there is the
    columns that have not reached the cell yet. A table that does not scroll
    keeps the strip: nothing is hidden there, and the rows really do end at
    the card.

    Done with `clip-path` rather than by narrowing the box: it clips what has
    been painted and changes no geometry, so the container keeps the exact
    shadow room it has always had and the scroll position cannot shift under
    it.
  */
  const [clipEdges, setClipEdges] = useState({ left: false, right: false });
  const updateClipEdges = () => {
    const el = bodyScrollRef.current;
    if (!el) return;
    // 1px of slack for the sub-pixel widths the column sizing leaves behind.
    const scrollable = el.scrollWidth - el.clientWidth > 1;
    setClipEdges({ left: scrollable && el.scrollLeft > 1, right: scrollable });
  };
  useEffect(() => {
    updateClipEdges();
    window.addEventListener("resize", updateClipEdges);
    return () => window.removeEventListener("resize", updateClipEdges);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tableMinWidth, visibleColumns.length, rows.length]);

  return (
    <>
      {openAdd && <AddAsset onClose={() => setOpenAdd(false)} />}

      {reviewingAsset && (
        <ApprovalModal
          subject="asset"
          partnerName={getPartner(userData)?.name}
          onClose={() => setReviewingAsset(null)}
          onStart={() => {
            const asset = reviewingAsset;
            setReviewingAsset(null);
            setAssetToEdit({ asset, focus: null });
          }}
        />
      )}
      {assetToEdit && (
        /*
          Only toEdit and onClose, matching how the Assets page mounts it.
          `openAssets` and `openBeneficiary` are boolean flags that scroll the
          modal to its beneficiary / trusted-advisor sections — passing
          functions made both truthy and opened the modal scrolled to the
          bottom with those sections highlighted.
        */
        <EditAsset
          toEdit={assetToEdit.asset}
          onClose={() => setAssetToEdit(null)}
          focusEvaluation={assetToEdit.focus}
        />
      )}

      {ownershipFor && (
        <OwnershipModal
          entry={
            ownersEntry(ownershipFor.asset) || { my_ownership: "", owners: [] }
          }
          ownerOptions={ownerOptionsFor(ownershipFor.asset)}
          onClose={() => setOwnershipFor(null)}
          onClear={() => {
            /*
              Staged like a save: the block is dropped when the row edit ends,
              so Escape puts it back.
            */
            setPendingOwnership({ id: ownershipFor.id, cleared: true });
            setOwnershipFor(null);
          }}
          onSave={(patch) => {
            /*
              Held on the row, not written here — persistRowEdit puts it into
              the asset's "other owners" data entry when the edit ends, so
              Escape discards it along with everything else in the row.
            */
            setPendingOwnership({ id: ownershipFor.id, ...patch });
            setOwnershipFor(null);
          }}
        />
      )}

      {peopleFor && (
        <PeopleModal
          beneficiaries={draft ? draft.beneficiaries : peopleFor.beneficiaries}
          trustees={draft ? draft.trustees : peopleFor.trustees}
          onClose={() => setPeopleFor(null)}
          onSave={(picked) => {
            /*
              Into the draft, not the asset: the row edit is what saves, so
              Escape discards this along with everything else typed in the row.
            */
            patchDraft(picked);
            setPeopleFor(null);
          }}
        />
      )}

      {debtFor && (
        <DebtModal
          asset={debtFor}
          onClose={() => setDebtFor(null)}
          onSave={(patch) => {
            /*
              Merged through mergeEvaluationIntoAsset, the same path the Add /
              Edit Asset modal uses, so the debt is described and its first
              reading recorded by exactly the same rules — dated today, stored
              negative — rather than by a second implementation that could drift.

              Held, not saved: the row edit is what writes it, and that edit can
              still be refused for want of an exposure or abandoned with Escape.
              persistRowEdit picks this up as the asset it commits.
            */
            const root = (userData && userData.UserData) || {};
            const { asset: updated } = mergeEvaluationIntoAsset(
              debtFor,
              {
                ...evaluationFromAsset(debtFor),
                has_associated_debt: true,
                ...patch,
              },
              root.financial_exposure_custom_options
            );
            /*
              The row stays in edit mode, and its draft was seeded before the
              debt existed — so without this the Debt Value cell would render
              empty immediately after entering one.
            */
            if (editingId) {
              patchDraft({ debt_amount: patch.debt_amount });
              setPendingDebt({ id: editingId, asset: updated });
            }
            setDebtFor(null);
          }}
        />
      )}

      <div className={`${styles.page} ${styles.page_wide}`}>
        {/*
          No report button here. The report is produced from the Dashboard, which
          is where the range and currency it is run for are chosen; a second
          button on this page could only generate the same document with settings
          the reader never saw.

          The title keeps its place either way — `.title_row` centres it and the
          button was positioned against that, not laid out beside it.
        */}
        <div
          className={`${styles.title_row} ${
            /* same 60/60 the other partner page titles get */
            partnerView && !inCustomerProfile
              ? partnerNavStyles.partner_title_row
              : ""
          }`}
        >
          {/* The profile supplies the heading when this page is shown inside one. */}
          {!inCustomerProfile && (
            <h1 className={styles.page_title}>{t("assetManagementText1")}</h1>
          )}
        </div>
        <PageTabs />

        <div className={styles.breadcrumb}>
          {/*
            Asset Management leads on both screens, and the pair keeps the same
            order on each — only which one is active changes. Swapping it here
            alone would have the two pages disagree about where each crumb sits.
          */}
          <span className={`${styles.crumb} ${styles.crumb_active}`}>
            {t("assetManagementText3")}
          </span>
          <span className={styles.crumb_separator}>|</span>
          {/* The Wealth Report — this page's dashboard view. */}
          <button
            type="button"
            className={styles.crumb}
            /*
              Inside a customer's profile this has to stay in the profile. The
              absolute path left it and showed the PARTNER's own wealth report
              under the customer's name.
            */
            onClick={() =>
              navigate(
                customerId
                  ? customerPath(customerId, "wealth")
                  : "/user/assetmanagement/dashboard"
              )
            }
          >
            {t("assetManagementText12")}
          </button>
        </div>

        <div className={styles.card}>
          <div className={styles.toolbar}>
            <div className={styles.search_wrap}>
              <span className={styles.search_icon} />
              <input
                type="text"
                className={styles.search}
                placeholder={t("assetManagementText9")}
                value={search}
                onChange={(e) => setSearch(e.target.value)}
              />
            </div>

            {/*
              Both filters take several values at once. The button names the
              first and counts the rest, the ✕ clears the lot, and the menu
              repeats "Clear filter" at the top so there is always a way back to
              unfiltered without hunting for the ticked boxes.
            */}
            <MultiSelectFilter
              label={t("assetManagementText5")}
              options={assetTypeFilterOptions}
              selected={assetTypes}
              onChange={setAssetTypes}
              clearLabel={t("assetManagementText40")}
            />

            <MultiSelectFilter
              label={t("assetManagementText6")}
              options={exposureFilterOptions}
              selected={exposures}
              onChange={setExposures}
              clearLabel={t("assetManagementText40")}
            />

            <div className={styles.fields_wrap} ref={fieldsRef}>
              <button
                type="button"
                className={styles.fields_button}
                onClick={() => setFieldsOpen((prev) => !prev)}
                aria-expanded={fieldsOpen}
              >
                <span>{t("assetManagementText10")}</span>
                <span className={styles.fields_caret}>▾</span>
              </button>
              {fieldsOpen && (
                <div className={styles.fields_menu}>
                  {columns
                    .filter((column) => !column.permanent)
                    .map((column) => (
                    <label key={column.key} className={styles.fields_option}>
                      <input
                        type="checkbox"
                        checked={isVisible(column.key)}
                        onChange={() => toggleColumn(column.key)}
                      />
                      <span>{column.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/*
              Adding is a permission a partner does have -- what they create
              waits for the customer's approval. Editing what the customer
              wrote is not, which is why this is not gated on disabledEditing.
            */}
            <button
              type="button"
              className={styles.add_button}
              disabled={Boolean(userData && userData.isFreezed)}
              onClick={() => setOpenAdd(true)}
            >
              <span>+</span>
              <span>{filterContent?.TableFilterText2 || "Add New Asset"}</span>
            </button>
          </div>

        </div>

        <div
          className={`${styles.table_scroll} ${styles.body_scroll} ${
            panning ? styles.panning : ""
          }`}
          /*
            Left off entirely on a table that fits, which is painted exactly as
            it was before any of this.
          */
          style={
            clipEdges.left || clipEdges.right
              ? {
                  clipPath: `inset(0 ${clipEdges.right ? 16 : 0}px 0 ${
                    clipEdges.left ? 16 : 0
                  }px)`,
                }
              : undefined
          }
          ref={bodyScrollRef}
          onScroll={updateClipEdges}
          {...panHandlers}
        >
            {/*
              The headings and the rows are ONE scroller.

              They used to be two, kept together by writing each one's
              scrollLeft into the other from its onScroll. That cannot keep up:
              the browser scrolls on the compositor thread and the event reaches
              the main thread afterwards, so the headings arrived 100-200ms late
              and then jumped to catch up -- measured from a screen recording at
              up to 219px behind in a single frame. Sharing one scroller makes
              the two move together with no JavaScript at all.

              Both tables already carry the same colGroup and the same
              minWidth, which is what lets their columns line up.
            */}
            <table
              className={`${styles.table} ${styles.header_table}`}
              style={{ minWidth: tableMinWidth }}
            >
              {colGroup}
              <thead>
                <tr>
                  {visibleColumns.map((column) => (
                    <th
                      key={column.key}
                      /*
                        The header pins the same two cells the body does, so
                        the two tables begin and end at the same place and the
                        headings disappear under them instead of running on
                        past them to the card's edges.

                        It was once left unpinned, because it holds no label
                        and -- being `:last-child` -- was the cell the card's
                        bottom corner was hung on, so it could not be made to
                        paint nothing. The corner moved to .header_scroll since
                        (see that rule), which leaves this free to be what it
                        now has to be: an opaque block that covers whatever is
                        scrolled under it.
                      */
                      className={`${column.sortable ? styles.th_sortable : ""} ${
                        column.key === ACTIONS_COLUMN.key
                          ? styles.cell_pinned_right
                          : ""
                      } ${
                        column.key === EDGE_COLUMN.key
                          ? styles.cell_pinned_left
                          : ""
                      }`}
                      onClick={
                        column.sortable ? () => toggleSort(column.key) : undefined
                      }
                    >
                      <span className={styles.th_label}>{column.label}</span>
                      {column.sortable && (
                        <span
                          className={styles.sort_icon}
                          style={{
                            backgroundImage: `url(${
                              sort.key === column.key
                                ? sort.desc
                                  ? downArrow
                                  : upArrow
                                : upDownArrow
                            })`,
                          }}
                        />
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
            </table>
          <table
            className={`${styles.table} ${styles.body_table}`}
            style={{ minWidth: tableMinWidth }}
          >
            {colGroup}
            <tbody
              onDragOver={(e) => {
                if (!dragId) return;
                // Without this the browser refuses the drop outright.
                e.preventDefault();
                e.dataTransfer.dropEffect = "move";
                setDragOverId(gapTargetAt(e.currentTarget, e.clientY));
              }}
              onDrop={(e) => {
                if (!dragId) return;
                e.preventDefault();
                /*
                  Recomputed from where the pointer actually is rather than
                  trusting the last dragover: a release can land a frame after
                  the last move, and dropping on a stale target puts the row
                  somewhere the gap was not.
                */
                const target = gapTargetAt(e.currentTarget, e.clientY);
                if (target && target !== dragId) moveRow(dragId, target);
                setDragId(null);
                setDragOverId(null);
                restingGapRef.current = null;
                releaseTableHeight();
              }}
            >
                {rows.length === 0 ? (
                  <tr>
                    <td
                      className={styles.empty_row}
                      colSpan={visibleColumns.length || 1}
                    >
                      {t("assetManagementText11")}
                    </td>
                  </tr>
                ) : (
                  rows.map((sourceAsset, index) => {
                    const id = rowId(sourceAsset, index);
                    /*
                      Percentages staged in the modal show in the two ownership
                      cells at once, rather than only after the row is closed.
                    */
                    const base = withOverrides(sourceAsset, index);
                    const asset =
                      pendingOwnership && pendingOwnership.id === id
                        ? mergeOwnership(base, pendingOwnership, `entry_local_${id}`)
                        : base;
                    const editing = id === editingId;
                    /*
                      The space the dragged row would drop into, opened just
                      above this one. Its own row so the gap falls BETWEEN two
                      cards rather than inside one -- see .gap_row.
                    */
                    const gap = (
                      <tr
                        className={`${styles.gap_row} ${
                          dragOverId === restingGapRef.current
                            ? styles.gap_row_resting
                            : ""
                        }`}
                        aria-hidden="true"
                      >
                        <td colSpan={visibleColumns.length} />
                      </tr>
                    );
                    return (
                    <React.Fragment key={id}>
                    {!compact && dragOverId === id && dragId !== id && gap}
                    <tr
                      data-row-id={id}
                      ref={editing ? editRowRef : undefined}
                      /*
                        Any cell opens the row.

                        Each cell used to carry its own onClick, and a column
                        left off that list -- Payoff Date -- simply did not
                        respond, even though nothing about it is special: it is
                        not editable itself, but clicking it should still open
                        the row so the editable columns can be reached. Asking
                        the row instead of each cell means a column cannot be
                        forgotten again.

                        Safe to put here because every control inside a row --
                        both action buttons, the pickers, the modal triggers --
                        already calls stopPropagation, so they keep their own
                        behaviour. beginEdit is a no-op once the row is open,
                        so a cell that still has its own handler does no harm
                        by firing both.
                      */
                      onClick={() => beginEdit(asset, index)}
                      /*
                        Hold and move to drag; a plain click still falls
                        through to the cell's onClick and opens edit mode,
                        because a drag only starts once the pointer moves.
                        Disabled while editing so text can be selected inside
                        the inputs.
                      */
                      draggable={!compact && !editing && !disabledEditing}
                      title={
                        !compact && !editing
                          ? t("assetManagementText21")
                          : undefined
                      }
                      onDragStart={(e) => {
                        /*
                          A sideways press scrolls the table instead of picking
                          the row up.

                          Two ways in. Usually a pointermove has already seen
                          the movement and started the pan, and this only has to
                          notice that and call the drag off — without which the
                          browser would take the press mid-pan and stop sending
                          the moves the pan is running on. Failing that, the
                          browser reached its own threshold first and this is
                          the last point at which the row can be left alone, so
                          the decision is made here from how far the press has
                          travelled.
                        */
                        /*
                          By now the decision is already made or it is not ours:
                          this event carries the position the press went down
                          at, so there is no direction in it to read. All that
                          is asked is whether a pan claimed the press first.
                        */
                        if (panRef.current) {
                          e.preventDefault();
                          return;
                        }
                        /*
                          The browser got there first, which it usually does.

                          PAN_MIN_TRAVEL is three CSS pixels, measured through
                          the DevTools protocol a pixel at a time. Chrome's own
                          threshold is in DEVICE pixels, so on a scaled display
                          it commits sooner than that -- observed at dx=1.71
                          with dy=0, a dead-straight sideways drag that the pan
                          was still calling "undecided" when the row was picked
                          up. Racing it cannot be won by choosing a smaller
                          number either: the number is in the wrong units.

                          So the race is not run. The press is judged here from
                          the travel WE recorded, and the row is handed back if
                          it turns out to have been going sideways. Any travel
                          counts now -- the browser has already decided this is
                          a drag, so the only question left is which one -- and
                          a tie still goes to the row, as it does above.
                        */
                        const last = panLastRef.current;
                        if (last && beginPanAt(last.x, last.y, 1)) {
                          e.preventDefault();
                          return;
                        }
                        e.dataTransfer.effectAllowed = "move";
                        // Firefox will not start a drag without a payload
                        e.dataTransfer.setData("text/plain", id);
                        /*
                          A frame later, not now. The browser photographs this
                          row for the image that follows the cursor, and it does
                          that after this handler returns -- collapsing the row
                          here meant it photographed an empty one, so the row
                          appeared to lose its contents rather than be picked up.

                          The gap is opened in the same breath, exactly where
                          this row is standing. The row is about to close to
                          nothing, and without something taking its place the
                          table would lose 48px and get it back the moment a
                          gap appeared somewhere else -- which is the jump up
                          and then down.
                        */
                        const next = rows[index + 1];
                        const restingGap = next
                          ? rowId(next, index + 1)
                          : DROP_AT_END;
                        restingGapRef.current = restingGap;
                        // Before anything collapses, so it records the full height.
                        holdTableHeight();
                        requestAnimationFrame(() => {
                          setDragId(id);
                          setDragOverId(restingGap);
                        });
                      }}
                      onDragEnd={() => {
                        setDragId(null);
                        setDragOverId(null);
                        restingGapRef.current = null;
                        releaseTableHeight();
                      }}
                      style={touchRowStyle(id, index)}
                      className={`${editing ? styles.row_editing : ""} ${
                        editingId && !editing ? styles.row_dimmed : ""
                      } ${
                        /*
                          Closed to nothing for a mouse, because the browser is
                          carrying its picture; picked up whole for a finger,
                          because nothing is.
                        */
                        dragId !== id
                          ? touchDrag
                            ? styles.row_shifting
                            : ""
                          : compact
                          ? styles.row_lifted
                          : styles.row_dragging
                      } ${
                        /*
                          Created by the partner, or changed by them and
                          waiting -- the row says "something here is for you"
                          either way.
                        */
                        needsReview(asset)
                          ? styles.row_awaiting_approval
                          : ""
                      }`}
                    >
                      {visibleColumns.map((column) => {
                        const raw = cellValue(asset, column.key);

                        /*
                          ---- the row's rounded start ----
                          Empty by design: everything it shows, it shows in
                          CSS. It still opens the row, because it is a piece of
                          the row and every other piece does -- with no column
                          to focus, since there is nothing in it to edit.
                        */
                        if (column.key === EDGE_COLUMN.key) {
                          return (
                            <td
                              key={column.key}
                              className={styles.cell_pinned_left}
                              onClick={() => beginEdit(asset, index)}
                            />
                          );
                        }

                        /*
                          ---- reorder: the drag grip ----
                          Touch only. Everything about the gesture lives on this
                          element so that the rest of the row keeps scrolling.
                        */
                        if (column.key === REORDER_COLUMN.key) {
                          return (
                            <td key={column.key} className={styles.cell_reorder}>
                              <span
                                role="button"
                                tabIndex={disabledEditing ? -1 : 0}
                                aria-label={t("assetManagementText21")}
                                title={t("assetManagementText21")}
                                className={`${styles.drag_handle} ${
                                  dragId === id ? styles.drag_handle_active : ""
                                }`}
                                onPointerDown={(event) =>
                                  startHandleDrag(event, id)
                                }
                                onPointerMove={moveHandleDrag}
                                onPointerUp={endHandleDrag}
                                onPointerCancel={endHandleDrag}
                              />
                            </td>
                          );
                        }

                        /*
                          ---- row action: open the asset ----
                          Always rendered, never editable, never hidden. Uses
                          the same edit icon as the inheritance tree
                          (Inheritance Tree/beneficiary.jsx) so the affordance
                          is consistent across the app.
                        */
                        if (column.key === "actions") {
                          return (
                            <td
                              key={column.key}
                              className={`${styles.cell_action} ${styles.cell_pinned_right}`}
                            >
                              <span
                                role="button"
                                tabIndex={0}
                                aria-label={t("assetManagementText39")}
                                title={t("assetManagementText39")}
                                /*
                                  The source SVG is fill="white" — invisible on
                                  this table. Drawn as a mask so the colour comes
                                  from CSS instead of the file, which leaves the
                                  shared asset untouched for the tree view.
                                */
                                style={{
                                  WebkitMaskImage: `url(${editIcon})`,
                                  maskImage: `url(${editIcon})`,
                                }}
                                className={styles.row_edit_icon}
                                onKeyDown={(event) => {
                                  if (event.key !== "Enter" && event.key !== " ")
                                    return;
                                  event.preventDefault();
                                  event.stopPropagation();
                                  commitEdit();
                                  setAssetToEdit({ asset });
                                }}
                                onClick={(event) => {
                                  // Do not let the click begin an inline edit
                                  // on the row underneath.
                                  event.stopPropagation();
                                  commitEdit();
                                  setAssetToEdit({ asset });
                                }}
                              />
                            </td>
                          );
                        }

                        /*
                          ---- ownership: modal, never inline ----

                          Both columns open the same modal: My Ownership is the
                          user's own share and Other Owners is everyone else's,
                          but they are two readings of one block, and the
                          percentages only make sense edited together.
                        */
                        if (
                          column.key === "my_ownership" ||
                          column.key === "other_owners"
                        ) {
                          /*
                            Two clicks, the same as the Debt Value cell: the
                            first opens the row, the second opens the modal.

                            It used to open the modal on the first click, which
                            meant these were the only two cells in the table
                            that could not put their row into edit mode — so
                            reaching them from the keyboard, or editing anything
                            else on the way past, behaved differently here.
                          */
                          if (!editing) {
                            return (
                              <td
                                key={column.key}
                                className={styles.cell_editable}
                                onClick={() => beginEdit(asset, index, column.key)}
                              >
                                <span
                                  className={styles.cell_truncate}
                                  title={raw || ""}
                                >
                                  {raw || EMPTY}
                                </span>
                              </td>
                            );
                          }
                          /*
                            While the row is locked every other cell turns into
                            a field, so this one has to look like one too —
                            otherwise it reads as the only thing not editable.
                            It still opens the modal rather than editing here.
                          */
                          return (
                            <td key={column.key}>
                              <button
                                type="button"
                                className={styles.cell_edit_button}
                                title={t("assetManagementText14")}
                                onClick={(event) => {
                                  /*
                                    A detour inside the edit, not a commit —
                                    exactly as the debt and people modals are.
                                  */
                                  event.stopPropagation();
                                  setOwnershipFor({ asset, id });
                                }}
                              >
                                <span className={styles.cell_truncate} title={raw || ""}>
                                  {raw || EMPTY}
                                </span>
                                <span
                                  className={styles.cell_edit_icon}
                                  aria-hidden="true"
                                />
                              </button>
                            </td>
                          );
                        }

                        /* ---- inline editors, active only for the locked row ---- */
                        if (editing && column.key === "asset_name") {
                          const clash = nameError === id;
                          return (
                            <td key={column.key}>
                              <input
                                ref={focusOn("asset_name")}
                                className={`${styles.cell_input} ${
                                  clash ? styles.cell_input_invalid : ""
                                }`}
                                value={draft.asset_name}
                                onChange={(e) => {
                                  patchDraft({ asset_name: e.target.value });
                                  // Clear as soon as they start fixing it.
                                  if (clash) setNameError(null);
                                }}
                              />
                            </td>
                          );
                        }

                        if (editing && column.key === "asset_type") {
                          /*
                            Same shape as the exposure cell: picking Custom
                            turns the cell into a free-text field, and the ↩
                            button puts the dropdown back.
                          */
                          if (draft.asset_type === ASSET_TYPE_CUSTOM) {
                            return (
                              <td key={column.key}>
                                <div className={styles.cell_custom}>
                                  <input
                                    className={`${styles.cell_input} ${
                                      typeNameRequired === id
                                        ? styles.cell_input_invalid
                                        : ""
                                    }`}
                                    placeholder={tEval(
                                      "assetEvaluationText26",
                                      "Enter a name"
                                    )}
                                    value={draft.asset_type_custom_label}
                                    ref={(el) => {
                                      if (el && assetTypeFocusRef.current) {
                                        assetTypeFocusRef.current = false;
                                        el.focus();
                                      }
                                    }}
                                    onChange={(e) => {
                                      // Clear as soon as it has a name.
                                      if (e.target.value.trim())
                                        setTypeNameRequired(null);
                                      patchDraft({
                                        asset_type_custom_label: e.target.value,
                                      });
                                    }}
                                  />
                                  <button
                                    type="button"
                                    className={styles.cell_custom_back}
                                    title={t("assetManagementText22")}
                                    onClick={() => {
                                      setTypeNameRequired(null);
                                      patchDraft({
                                        asset_type: canonicalAssetType(
                                          asset.asset_type
                                        ),
                                        asset_type_custom_label: "",
                                      });
                                    }}
                                  >
                                    ↩
                                  </button>
                                </div>
                              </td>
                            );
                          }
                          return (
                            <td key={column.key}>
                              {/*
                                The app's own dropdown rather than a native
                                <select>: a native popup is painted outside the
                                page and sizes itself to its contents, so its
                                height cannot be capped. This one scrolls inside
                                CELL_MENU_HEIGHT, opens below when there is room
                                and above when there is not, and wears the
                                cell's field styling so the closed control reads
                                as the rest of the row does.

                                Choosing a value does not end the row edit — see
                                the click-away handler, which treats the portaled
                                menu as part of the row.
                              */}
                              <Dropdown
                                ref={focusOn("asset_type")}
                                value={draft.asset_type || ""}
                                onChange={(next) => {
                                  assetTypeFocusRef.current =
                                    next === ASSET_TYPE_CUSTOM;
                                  patchDraft({
                                    asset_type: next,
                                    // Drop a stale name when leaving Custom.
                                    ...(next === ASSET_TYPE_CUSTOM
                                      ? {}
                                      : { asset_type_custom_label: "" }),
                                  });
                                }}
                                groups={assetTypeGroups}
                                placeholder={EMPTY}
                                triggerClassName={styles.cell_trigger}
                                maxMenuHeight={CELL_MENU_HEIGHT}
                                ariaLabel={t("assetManagementText5")}
                                title={
                                  draft.asset_type
                                    ? getAssetTypeTranslated(
                                        draft.asset_type,
                                        filterContent,
                                        dublicatedcontent
                                      )
                                    : ""
                                }
                              />
                            </td>
                          );
                        }

                        /*
                          Same dropdown as the Add / Edit Asset modal. Picking
                          "Custom" swaps this one cell over to a free-text input
                          for the name — there is no room beside it for a second
                          field the way there is in the modal. The ↩ button goes
                          back to the list.
                        */
                        if (editing && column.key === "financial_exposure") {
                          const isCustom =
                            draft.financial_exposure ===
                            FINANCIAL_EXPOSURE_CUSTOM;
                          if (isCustom) {
                            return (
                              <td key={column.key}>
                                <div className={styles.cell_custom}>
                                  <input
                                    /*
                                      The red border this cell never had. Every
                                      other inline error draws one; this one
                                      relied on its message alone, so dropping
                                      the message would have left it with no
                                      feedback at all.
                                    */
                                    className={`${styles.cell_input} ${
                                      exposureRequired?.id === id
                                        ? styles.cell_input_invalid
                                        : ""
                                    }`}
                                    placeholder={tEval(
                                      "assetEvaluationText26",
                                      "Enter a name"
                                    )}
                                    value={draft.financial_exposure_custom_label}
                                    ref={(el) => {
                                      if (el && exposureFocusRef.current) {
                                        exposureFocusRef.current = false;
                                        el.focus();
                                      }
                                    }}
                                    onChange={(e) => {
                                      if (e.target.value.trim())
                                        setExposureRequired(null);
                                      patchDraft({
                                        financial_exposure_custom_label:
                                          e.target.value,
                                      });
                                    }}
                                  />
                                  <button
                                    type="button"
                                    className={styles.cell_custom_back}
                                    title={t("assetManagementText22")}
                                    onClick={() =>
                                      patchDraft({
                                        financial_exposure: "",
                                        financial_exposure_custom_label: "",
                                      })
                                    }
                                  >
                                    ↩
                                  </button>
                                </div>
                              </td>
                            );
                          }
                          const exposureOptions = exposureOptionsFor(
                            asset,
                            draft.financial_exposure
                          );
                          return (
                            <td key={column.key}>
                              {/*
                                The app's own dropdown, as on Asset Type: a
                                native popup cannot be capped, and this list
                                carries every built-in exposure plus the user's
                                own. Scrolls inside CELL_MENU_HEIGHT, opens above
                                when there is no room below, and choosing a value
                                leaves the row in edit mode — the click-away
                                handler treats the portaled menu as part of the
                                row.
                              */}
                              <Dropdown
                                ref={focusOn("financial_exposure")}
                                value={draft.financial_exposure || ""}
                                onChange={(next) => {
                                  exposureFocusRef.current =
                                    next === FINANCIAL_EXPOSURE_CUSTOM;
                                  /*
                                    "Custom" is not an exposure until it is
                                    named, so the error stays up until the name
                                    input above clears it.
                                  */
                                  if (next && next !== FINANCIAL_EXPOSURE_CUSTOM)
                                    setExposureRequired(null);
                                  patchDraft({
                                    financial_exposure: next,
                                    // Drop a stale name when leaving Custom.
                                    ...(next === FINANCIAL_EXPOSURE_CUSTOM
                                      ? {}
                                      : { financial_exposure_custom_label: "" }),
                                  });
                                }}
                                groups={[{ label: "", options: exposureOptions }]}
                                placeholder={tEval(
                                  "assetEvaluationText16",
                                  "Select"
                                )}
                                triggerClassName={styles.cell_trigger}
                                className={
                                  exposureRequired?.id === id
                                    ? styles.cell_input_invalid
                                    : ""
                                }
                                maxMenuHeight={CELL_MENU_HEIGHT}
                                ariaLabel={t("assetManagementText6")}
                                title={
                                  exposureLabel(draft.financial_exposure) || ""
                                }
                              />
                            </td>
                          );
                        }

                        if (
                          editing &&
                          (column.key === "asset_value" ||
                            column.key === "debt_value")
                        ) {
                          const isDebtField = column.key === "debt_value";
                          const field = isDebtField
                            ? "debt_amount"
                            : "asset_value";
                          /*
                            Marks whichever of the two cells tried to record a
                            figure with no exposure. The red border is the whole
                            of the message now -- the table says nothing in
                            words.
                          */
                          const needsExposure =
                            exposureRequired?.id === id &&
                            exposureRequired.field === column.key;
                          /*
                            With no history to edit there is nothing for an
                            inline edit to attach to: an asset value needs a
                            first reading, and a debt needs a payoff date, and
                            neither can be supplied from a table cell. So the
                            cell offers the modal instead of an input.
                          */
                          /*
                            Only a debt still sends the user to the modal: a
                            debt record is keyed by its payoff date, and there
                            is nowhere in a table cell to enter one. A first
                            valuation needs no such thing — it takes today's
                            date and the language's currency — so Asset Value
                            stays editable here even with no history.
                          */
                          /*
                            A debt amount is a reading, and a reading needs the
                            description it is a reading OF — type, payoff date,
                            interest — none of which fit in a table cell. With
                            no debt on the asset yet, the cell opens a debt
                            modal that asks for the whole thing at once, the
                            same way ownership opens its own.
                          */
                          /*
                            A debt held from the modal but not yet saved counts
                            as one: the cell has an amount to show and to edit,
                            so it becomes an input like any other debt rather
                            than still offering to add one.
                          */
                          const debtAsset =
                            pendingDebt?.id === id ? pendingDebt.asset : asset;
                          const series = debtAsset.asset_debt_records;
                          if (
                            isDebtField &&
                            (!Array.isArray(series) || series.length === 0)
                          ) {
                            return (
                              <td key={column.key}>
                                <button
                                  type="button"
                                  className={`${styles.cell_edit_button} ${
                                    needsExposure
                                      ? styles.cell_input_invalid
                                      : ""
                                  }`}
                                  onClick={(event) => {
                                    /*
                                      Deliberately does NOT commit the row. The
                                      debt modal is a detour within the edit, so
                                      the row stays locked while it is open and
                                      after it saves — clicking away is what
                                      ends the edit, as with any other cell.
                                    */
                                    event.stopPropagation();
                                    /*
                                      Opens whether or not the asset has an
                                      exposure yet. The debt is entered freely
                                      and kept; the exposure is only insisted on
                                      when the row edit ends, so the user is not
                                      stopped at the door of a modal they have
                                      not filled in yet.
                                    */
                                    setDebtFor(debtAsset);
                                  }}
                                >
                                  {raw || EMPTY}
                                </button>
                              </td>
                            );
                          }

                          return (
                            <td key={column.key}>
                              {/*
                                The border sits on the wrapper rather than the
                                two controls, so the currency and the number
                                read as one field — and so the currency can be
                                as wide as its sign needs ("$" and "CHF" are
                                not the same width).
                              */}
                              <span
                                className={`${styles.cell_money} ${
                                  needsExposure
                                    ? styles.cell_input_invalid
                                    : ""
                                }`}
                              >
                                <span
                                  className={styles.cell_money_prefix}
                                  aria-hidden="true"
                                >
                                  {currencySign(
                                    inlineCurrencyFor(debtAsset, isDebtField)
                                  )}
                                </span>
                                <input
                                  ref={focusOn(column.key)}
                                  inputMode="numeric"
                                  className={styles.cell_input}
                                  placeholder={isDebtField ? "-0" : "0"}
                                  /* Grouped with commas, exactly as the Add /
                                     Edit Asset modal formats the same figures.
                                     The handlers below strip every non-digit, so
                                     the separator never reaches state. */
                                  value={displayAmount(draft[field])}
                                  /*
                                    A debt is always a liability, so its sign is
                                    pinned negative here exactly as it is in the
                                    Add / Edit Asset modal, and the caret is kept
                                    at the end so typing does not land in front of
                                    the minus.
                                  */
                                  onChange={(e) => {
                                    const input = e.currentTarget;
                                    if (!isDebtField) {
                                      patchDraft({
                                        asset_value: input.value.replace(
                                          /[^\d.-]/g,
                                          ""
                                        ),
                                      });
                                      return;
                                    }
                                    const digits = input.value.replace(/\D/g, "");
                                    patchDraft({
                                      debt_amount:
                                        digits === "" ? "" : `-${digits}`,
                                    });
                                    window.requestAnimationFrame(() => {
                                      if (document.activeElement === input) {
                                        const end = input.value.length;
                                        input.setSelectionRange(end, end);
                                      }
                                    });
                                  }}
                                />
                              </span>
                            </td>
                          );
                        }

                        /* ---- liquid: the modal's switch, as a two-way cell ---- */
                        if (editing && column.key === "is_liquid") {
                          return (
                            <td key={column.key}>
                              <select
                                ref={focusOn("is_liquid")}
                                className={styles.cell_select}
                                value={draft.is_liquid ? "yes" : "no"}
                                onChange={(e) =>
                                  patchDraft({
                                    is_liquid: e.target.value === "yes",
                                  })
                                }
                              >
                                <option value="no">
                                  {tEval("assetEvaluationText17", "No")}
                                </option>
                                <option value="yes">
                                  {tEval("assetEvaluationText18", "Yes")}
                                </option>
                              </select>
                            </td>
                          );
                        }

                        /*
                          ---- the debt's description ----

                          Type, interest and payoff date describe a debt, so
                          they only mean something once there is one. With no
                          debt on the asset the cell offers the debt modal, the
                          same as the Debt Value cell beside it — filling a
                          payoff date into an asset that owes nothing would
                          leave half a debt behind.
                        */
                        if (
                          editing &&
                          (column.key === "debt_type" ||
                            column.key === "debt_annual_interest" ||
                            column.key === "debt_payoff_date")
                        ) {
                          const debtAsset =
                            pendingDebt?.id === id ? pendingDebt.asset : asset;
                          if (!hasDebt(debtAsset)) {
                            return (
                              <td key={column.key}>
                                <button
                                  type="button"
                                  className={styles.cell_edit_button}
                                  onClick={(event) => {
                                    // As with Debt Value: a detour, not a commit.
                                    event.stopPropagation();
                                    setDebtFor(debtAsset);
                                  }}
                                >
                                  {EMPTY}
                                </button>
                              </td>
                            );
                          }

                          if (column.key === "debt_type") {
                            return (
                              <td key={column.key}>
                                <select
                                  ref={focusOn("debt_type")}
                                  className={styles.cell_select}
                                  value={draft.debt_type}
                                  onChange={(e) =>
                                    patchDraft({ debt_type: e.target.value })
                                  }
                                >
                                  <option value="">
                                    {tEval("assetEvaluationText16", "Select")}
                                  </option>
                                  {DEBT_TYPE_GROUPS.map((group) => (
                                    <optgroup
                                      key={group.labelKey}
                                      label={tEval(group.labelKey, "")}
                                    >
                                      {group.options.map((option) => (
                                        <option
                                          key={option.value}
                                          value={option.value}
                                        >
                                          {tEval(option.labelKey, option.value)}
                                        </option>
                                      ))}
                                    </optgroup>
                                  ))}
                                </select>
                              </td>
                            );
                          }

                          if (column.key === "debt_annual_interest") {
                            return (
                              <td key={column.key}>
                                {/*
                                  The "%" is drawn beside the field rather than
                                  put inside its value. Rendering it into the
                                  text is what makes Backspace look broken —
                                  the caret lands behind a character that is
                                  stripped out of every keystroke, so the press
                                  does nothing (see onPercentChange in
                                  OwnershipModal.jsx). An adornment cannot be
                                  typed into or deleted, so the question never
                                  comes up.
                                */}
                                <span className={styles.cell_money}>
                                  <input
                                    ref={focusOn("debt_annual_interest")}
                                    inputMode="decimal"
                                    className={styles.cell_input}
                                    placeholder="0"
                                    value={draft.debt_annual_interest}
                                    onChange={(e) =>
                                      patchDraft({
                                        // Digits and one separator, as the modal takes.
                                        debt_annual_interest:
                                          e.target.value.replace(/[^\d.]/g, ""),
                                      })
                                    }
                                  />
                                  <span
                                    className={styles.cell_money_suffix}
                                    aria-hidden="true"
                                  >
                                    %
                                  </span>
                                </span>
                              </td>
                            );
                          }

                          /*
                            Stored YYYY/MM/DD, which a date input will not
                            accept — converted at the boundary, both ways.
                          */
                          return (
                            <td key={column.key}>
                              <input
                                ref={focusOn("debt_payoff_date")}
                                type="date"
                                className={styles.cell_input}
                                value={toInputDate(draft.debt_payoff_date)}
                                onChange={(e) =>
                                  patchDraft({
                                    debt_payoff_date: toStoredDate(
                                      e.target.value
                                    ),
                                  })
                                }
                              />
                            </td>
                          );
                        }

                        if (editing && column.key.startsWith("custom:")) {
                          const title = column.key.slice("custom:".length);
                          return (
                            <td key={column.key}>
                              <input
                                ref={focusOn(column.key)}
                                className={styles.cell_input}
                                value={draft.custom[title] || ""}
                                onChange={(e) =>
                                  patchDraft({
                                    custom: {
                                      ...draft.custom,
                                      [title]: e.target.value,
                                    },
                                  })
                                }
                              />
                            </td>
                          );
                        }

                        /* ---- read-only rendering ---- */

                        /*
                          People attached to the asset, drawn as the same
                          avatars the Assets table uses (Beneficiarytrustee) so
                          the two pages read alike. Not editable here: choosing
                          who inherits an asset is a picker over other records,
                          which is what the asset modal is for.
                        */
                        if (
                          column.key === "beneficiaries" ||
                          column.key === "trustees"
                        ) {
                          /*
                            While the row is locked the cell shows whatever the
                            picker has staged, not what is saved — otherwise the
                            avatars would not change until the edit ended.
                          */
                          const people = peopleOn(
                            editing ? { ...asset, ...draft } : asset,
                            column.key
                          );
                          /*
                            Like My Ownership: it has to look editable while the
                            row is locked, but the choosing happens in a modal.
                          */
                          if (editing) {
                            return (
                              <td key={column.key}>
                                <button
                                  type="button"
                                  /*
                                    No field box on these two. The cell holds
                                    avatars, not a value being typed, and the
                                    frame around them read as an empty input
                                    sitting behind the faces.
                                  */
                                  className={`${styles.cell_edit_button} ${styles.cell_people_button}`}
                                  onClick={(event) => {
                                    // A detour inside the edit, not a commit.
                                    event.stopPropagation();
                                    setPeopleFor({ asset, id });
                                  }}
                                >
                                  {people.length === 0 ? (
                                    EMPTY
                                  ) : (
                                    <span className={styles.people_cell}>
                                      {people.slice(0, 3).map((person) => (
                                        <Beneficiarytrustee
                                          key={person.user_id}
                                          name={person.name}
                                          priority={person.priority}
                                          deleatable={false}
                                          avatar={person.avatar}
                                          styling={{
                                            width: "32px",
                                            height: "32px",
                                          }}
                                          textFontSize="11px"
                                        />
                                      ))}
                                      {people.length > 3 && (
                                        <span className={styles.people_more}>
                                          +{people.length - 3}
                                        </span>
                                      )}
                                    </span>
                                  )}
                                </button>
                              </td>
                            );
                          }
                          return (
                            <td
                              key={column.key}
                              className={styles.cell_editable}
                              onClick={() => beginEdit(asset, index, column.key)}
                            >
                              {people.length === 0 ? (
                                EMPTY
                              ) : (
                                <span className={styles.people_cell}>
                                  {/*
                                    Capped so a widely-shared asset cannot push
                                    the column open; the rest are counted. Same
                                    idea as the Assets table's cap, but stated
                                    rather than silently dropping them.
                                  */}
                                  {people.slice(0, 3).map((person) => (
                                    <Beneficiarytrustee
                                      key={person.user_id}
                                      name={person.name}
                                      priority={person.priority}
                                      deleatable={false}
                                      avatar={person.avatar}
                                      styling={{ width: "32px", height: "32px" }}
                                      textFontSize="11px"
                                    />
                                  ))}
                                  {people.length > 3 && (
                                    <span
                                      className={styles.people_more}
                                      title={people
                                        .slice(3)
                                        .map((p) => p.name)
                                        .join(", ")}
                                    >
                                      +{people.length - 3}
                                    </span>
                                  )}
                                </span>
                              )}
                            </td>
                          );
                        }

                        /* Editable asset-level fields with nothing special to draw. */
                        if (
                          column.key === "is_liquid" ||
                          column.key === "debt_type" ||
                          column.key === "debt_annual_interest" ||
                          column.key === "debt_payoff_date"
                        ) {
                          return (
                            <td
                              key={column.key}
                              className={styles.cell_editable}
                              onClick={() => beginEdit(asset, index, column.key)}
                            >
                              {raw || EMPTY}
                            </td>
                          );
                        }

                        if (column.key === "asset_name") {
                          return (
                            <td
                              key={column.key}
                              className={styles.cell_editable}
                              onClick={() => beginEdit(asset, index, column.key)}
                            >
                              <span className={styles.asset_name_cell}>
                                <span
                                  className={styles.asset_logo}
                                  style={{
                                    backgroundImage: `url(${selectLogo(
                                      asset.asset_type
                                    )})`,
                                  }}
                                />
                                <span
                                  className={styles.cell_truncate}
                                  title={raw || ""}
                                >
                                  {raw || EMPTY}
                                </span>
                                {/*
                                  Created by the partner and not approved yet.
                                  Until it is, the asset is in no total and no
                                  beneficiary inherits it.
                                */}
                                {needsReview(asset) && (
                                  <button
                                    type="button"
                                    className={styles.review_marker}
                                    onClick={(event) => {
                                      event.stopPropagation();
                                      // Visible to the partner, actionable
                                      // only by the customer.
                                      if (userData.partnerReadOnly) return;
                                      setReviewingAsset(asset);
                                    }}
                                  >
                                    <span aria-hidden="true">!</span>
                                    {approvalContent?.partnerApprovalText13}
                                  </button>
                                )}
                              </span>
                            </td>
                          );
                        }

                        if (column.key === "asset_type") {
                          return (
                            <td
                              key={column.key}
                              className={styles.cell_editable}
                              onClick={() => beginEdit(asset, index, column.key)}
                            >
                              {raw
                                ? getAssetTypeTranslated(
                                    raw,
                                    filterContent,
                                    dublicatedcontent
                                  )
                                : EMPTY}
                            </td>
                          );
                        }

                        if (
                          column.key === "asset_value" ||
                          column.key === "debt_value"
                        ) {
                          const isDebt = column.key === "debt_value";
                          const amount = isDebt
                            ? debtAmountOf(asset)
                            : Number(asset.asset_value);
                          const known =
                            raw !== null &&
                            amount !== null &&
                            !Number.isNaN(amount);
                          return (
                            <td
                              key={column.key}
                              className={styles.cell_editable}
                              onClick={() => beginEdit(asset, index, column.key)}
                            >
                              <span
                                className={
                                  !known
                                    ? styles.value_muted
                                    : amount < 0
                                    ? styles.value_negative
                                    : styles.value_positive
                                }
                              >
                                {raw || EMPTY}
                              </span>
                            </td>
                          );
                        }

                        if (
                          column.key === "financial_exposure" ||
                          column.key.startsWith("custom:")
                        ) {
                          return (
                            <td
                              key={column.key}
                              className={styles.cell_editable}
                              onClick={() => beginEdit(asset, index, column.key)}
                            >
                              {raw || EMPTY}
                            </td>
                          );
                        }

                        return <td key={column.key}>{raw || EMPTY}</td>;
                      })}
                    </tr>
                    {/* The one gap no row sits below: after the last one. */}
                    {dragOverId === DROP_AT_END &&
                      index === rows.length - 1 &&
                      gap}
                    </React.Fragment>
                    );
                  })
                )}
            </tbody>
          </table>
        </div>

        {/*
          The floor under the table.

          A spacer below the scroller rather than a `min-height` on it: the
          horizontal scrollbar is drawn at the BOTTOM of that box, so a
          min-height would strand it hundreds of pixels beneath the one row it
          belongs to. This keeps the bar with the rows and puts the empty
          space after it, where there is nothing to look at anyway.
        */}
        {floorHeight > 0 && (
          <div style={{ height: `${floorHeight}px` }} aria-hidden="true" />
        )}
      </div>
    </>
  );
}

export default AssetManagement;
